import { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useFormGuard } from "@/hooks/useFormGuard";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { AddressSearchField, type PlaceAddressData } from "@/components/ui/AddressSearchField";
import { MapPinPicker } from "@/components/ui/MapPinPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Minus, Loader2, Calendar as CalendarIcon, X, Pencil, Building2, User } from "lucide-react";
import { ImageUpload } from "@/components/ui/image-upload";
import { IMAGE_OR_PDF_ACCEPT, IMAGE_OR_VIDEO_ACCEPT, IMAGE_VIDEO_OR_PDF_ACCEPT } from "@/lib/storage";
import { hijriDateString } from "@/lib/hijri";
import { MAX_GALLERY_IMAGES } from "@/lib/constants";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateProperty, useUpdateProperty } from "@/hooks/useProperties";
import { useCreateUnit } from "@/hooks/useUnits";
import { useOwners, useOwnerEntityMeta } from "@/hooks/useOwners";
import {
  isOrganizationOwner,
  getOwnerEntityDisplayName,
  getOwnerRepresentativeName,
  type OwnerEntityMeta,
} from "@/lib/ownerHelpers";
import { Badge } from "@/components/ui/badge";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionLimits, useUnitUsage } from "@/hooks/useSubscription";
import { UpgradeModal } from "@/components/subscription";
import { toast } from "sonner";
import {
  PROPERTY_PURPOSES,
  getPropertyTypesForPurpose,
  getUnitTypesForPropertyType,
  getUnitTypesForPurpose,
  getPropertyTypeLabel,
  getUnitTypeLabel,
  isSingleUnitProperty,
  normalizePropertyPurpose,
} from "@/lib/propertyUnitMatrix";
import { useNafathIdentity } from "@/hooks/useNafathIdentity";
import { usePermissions } from "@/hooks/usePermissions";
import { AddOwnerDialog } from "@/components/owners/AddOwnerDialog";
import { propertyFormSchema, type PropertyFormValues } from "@/lib/propertyFormSchema";
import { OWNERSHIP_TYPES } from "@/lib/ownershipTypes";
import { contractsRepository, unitsRepository } from "@/repositories";

interface AddPropertyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editProperty?:
    | (Property & {
        name_en?: string | null;
        postal_code?: string | null;
        // Not yet in the generated Supabase types (added by the property public
        // page settings migration); typed here until types.ts is regenerated.
        public_units_source?: string | null;
      })
    | null;
}

type Property = Database["public"]["Tables"]["properties"]["Row"];

// Counter component for +/- inputs
function CounterInput({
  label,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full"
          onClick={() => onChange(Math.max(min, value - 1))}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="w-8 text-center font-medium">{value}</span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => onChange(value + 1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// Resolve the unit-type options for a (unit_purpose × property_type) pair.
// The matrix only covers the "obvious" combinations (residential building,
// commercial center, multi_purpose compound, …). For inherently-flexible
// shells like a multi_purpose building, a user picking unit_purpose=medical
// has no matrix row — so we fall back to the purpose-only allowed list
// (UNIT_TYPES_BY_PURPOSE) instead of leaving the dropdown empty.
export function resolveAvailableUnitTypes(unitPurpose: string, propertyType: string): string[] {
  if (!unitPurpose || !propertyType) return [];
  const matrixTypes = getUnitTypesForPropertyType(unitPurpose, propertyType);
  if (matrixTypes.length > 0) return matrixTypes;
  return getUnitTypesForPurpose(unitPurpose);
}

// Cascade rule (per product requirement): when a property switches from
// multi_purpose to a single purpose, every unit under it must inherit the new
// purpose so we don't end up with stale residential units inside what's now a
// commercial-only property. We also cascade on single→single transitions
// because the AddUnitDialog locks the unit purpose to the parent's purpose
// for non-multi properties — so any drift would already be invisible to the
// user and surface only at VAT calculation time.
// Going single→multi does NOT cascade: existing unit purposes are kept and
// become individually editable.
/**
 * When a property's owner changes, propagate the new owner_id to all DRAFT
 * and PENDING contracts for that property. Active/expired/terminated contracts
 * keep their historical owner snapshot — only mutable pre-final contracts get
 * updated.
 *
 * Called in both the direct-save path (simple owner swap) and the sync-dialog
 * confirm path (multiple→single ownership change). Both paths already update
 * the property row and optionally the units; this ensures the contracts list
 * shows the current owner immediately without the user having to delete and
 * recreate contracts.
 *
 * Multi-tenant: always filters by company_id so no cross-tenant leakage.
 */
async function propagateDraftContractOwnerIfNeeded(
  propertyId: string,
  newOwnerId: string,
  previousOwnerId: string | null | undefined,
  companyId: string,
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  // Short-circuit when the owner FK hasn't actually changed.
  if (!newOwnerId || newOwnerId === previousOwnerId) return;

  // The DB write lives in the contracts repository (the slice's sole data-access
  // home — Strangler-Fig); the component only owns the cache invalidation below.
  await contractsRepository.propagateDraftContractOwner(propertyId, newOwnerId, companyId);

  // Invalidate all contract-list views so they refetch with the new owner.
  queryClient.invalidateQueries({ queryKey: ["contracts"] });
  queryClient.invalidateQueries({ queryKey: ["contracts-stats"] });
}

async function cascadeUnitPurposesIfNeeded(
  editProperty: { id: string; purpose: string | null } | null,
  newPurpose: string,
  companyId: string,
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  if (!editProperty || !newPurpose) return;
  const oldPurpose = editProperty.purpose ?? "";
  if (oldPurpose === newPurpose) return;
  if (newPurpose === "multi_purpose") return; // single→multi: leave units alone

  await unitsRepository.updatePurposeByProperty(
    editProperty.id,
    companyId,
    newPurpose,
  );
  queryClient.invalidateQueries({ queryKey: ["units"] });
  queryClient.invalidateQueries({ queryKey: ["units-with-contracts"] });
  queryClient.invalidateQueries({ queryKey: ["property", editProperty.id] });
}

// Pending unit type mirrors the form fields used on the unit tab
interface PendingUnit {
  unitPurpose: string;
  unitType: string;
  unitName: string;
  unitArea: string;
  unitImages: string[];
  annualRent: string;
  deposit: string;
  saleAmount: string;
  floorNumber: string;
  isMezzanine: boolean;
  frontSideLength: string;
  finishing: string;
  furnishingStatus: string;
  bedrooms: number;
  bathrooms: number;
  majles: number;
  kitchens: number;
  maidRooms: number;
  storageRooms: number;
  outdoorAreas: number;
  utilities: { electricity: string; water: string; gas: string };
  highlights: { counters: Record<string, number>; checkboxes: Record<string, boolean> };
  owner_id?: string;
  legal?: {
    ownershipType: string;
    ownershipNumber: string;
    ownershipDate: string | undefined;
    // F11: Hijri date for unit deed
    ownershipDateHijri?: string;
    infoRestriction: string | null;
    mortgageRestriction: string | null;
    obligations: string | null;
    propertyDispute: string | null;
    ownershipDocuments: string[];
  };
}

export function AddPropertyDialog({ open, onOpenChange, editProperty }: AddPropertyDialogProps) {
  const { t, language } = useLanguage();
  const { companyId, isLoading: authLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const canCreateUnits = hasPermission("units.create") || hasPermission("units");
  const canCreateOwners = hasPermission("owners.create");
  const queryClient = useQueryClient();
  const createProperty = useCreateProperty();
  const updateProperty = useUpdateProperty();
  const createUnit = useCreateUnit();
  const { nafathIdentity } = useNafathIdentity();
  const { data: owners = [] } = useOwners();
  // Entity metadata (individual vs organization, establishment + representative
  // names) keyed by owner id — the owners list itself doesn't carry these.
  const { data: ownerMeta = {} } = useOwnerEntityMeta();

  // A small chip distinguishing an organization owner from an individual,
  // shown in the owner pickers. Memoised on `t` so it isn't recreated each render.
  const ownerTypeChip = useCallback(
    (meta: OwnerEntityMeta | null | undefined) =>
      isOrganizationOwner(meta) ? (
        <Badge variant="outline" className="gap-1 whitespace-nowrap text-[10px] py-0">
          <Building2 className="h-3 w-3" />
          {t("منشأة", "Organization")}
        </Badge>
      ) : (
        <Badge variant="secondary" className="gap-1 whitespace-nowrap text-[10px] py-0">
          <User className="h-3 w-3" />
          {t("فرد", "Individual")}
        </Badge>
      ),
    [t],
  );
  const isEditMode = !!editProperty;

  // ── React Hook Form setup ───────────────────────────────────────────────────
  const form = useForm<PropertyFormValues>({
    resolver: zodResolver(propertyFormSchema),
    defaultValues: {
      purpose: "",
      propertyType: "",
      name: "",
      ownershipType: "single",
      nameEn: "",
      ownerId: null,
      description: "",
      descriptionEn: "",
      publicUnitsSource: "listings",
      area: "",
      address: "",
      city: "",
      district: "",
      postalCode: "",
      compoundName: "",
      compoundCode: "",
      latitude: null,
      longitude: null,
      images: [],
      floorPlanImages: [],
      information: {
        shortAddress: "",
        nationalAddress: "",
        country: "SA",
        region: "",
        streetName: "",
        buildingNumber: "",
        secondaryNumber: "",
        streetWidth: "",
        landNumber: "",
        planNumber: "",
        propertyAge: "",
        direction: "",
        propertyValue: "",
        communityName: "",
        floorsCount: 1,
        unitsPerFloor: 1,
        parkingLots: 1,
        elevators: 1,
      },
      utilities: [],
      highlights: {
        features: [],
        building: [],
        healthFitness: [],
        recreation: [],
        cleaning: [],
        businessSecurity: [],
        technology: [],
        miscellaneous: [],
      },
      legal: {
        ownershipType: "",
        ownershipNumber: "",
        ownershipDate: "",
        infoRestriction: null,
        mortgageRestriction: null,
        obligations: null,
        propertyDispute: null,
        ownershipDocuments: [],
      },
      units: [],
      // Unit tab current-unit fields
      unitPurpose: "",
      unitType: "",
      unitName: "",
      unitArea: "",
      unitImages: [],
      annualRent: "",
      deposit: "",
      saleAmount: "",
      floorNumber: "",
      isMezzanine: false,
      frontSideLength: "",
      finishing: "finished",
      furnishingStatus: "unfurnished",
      bedrooms: 1,
      bathrooms: 1,
      majles: 1,
      kitchens: 1,
      maidRooms: 0,
      storageRooms: 0,
      outdoorAreas: 0,
      unitElectricityAccount: "",
      unitWaterAccount: "",
      unitGasMeter: "",
      unitCentralAC: 0,
      unitSplitAC: 0,
      unitDesertCooler: 0,
      unitWindowAC: 0,
      unitParkingCount: 0,
      unitHasHeating: false,
      unitHasKitchenCabinets: false,
      unitHasKitchenSink: false,
      unitHasDriversRoom: false,
      unitIsSmartHome: false,
      unitHasBalcony: false,
      unitHasParkingInside: false,
      unitHasParkingOutside: false,
      unitLegalOwnershipType: "",
      unitOwnershipNumber: "",
      unitOwnershipDate: "",
      unitHasInfoRestriction: false,
      unitInfoRestrictionText: "",
      unitHasMortgageRestriction: false,
      unitMortgageRestrictionText: "",
      unitHasObligations: false,
      unitObligationsText: "",
      unitHasPropertyDispute: false,
      unitPropertyDisputeText: "",
      unitOwnershipDocuments: [],
      unitOwnerId: null,
    },
  });

  const { register, watch, setValue, reset, formState: { errors } } = form;

  // ── Watched values (scoped per-field to avoid full re-render on every keystroke) ──
  const propertyPurpose = watch("purpose");
  const propertyType = watch("propertyType");
  const propertyName = watch("name");
  const nameEn = watch("nameEn") ?? "";
  const description = watch("description") ?? "";
  const descriptionEn = watch("descriptionEn") ?? "";
  const publicUnitsSource = watch("publicUnitsSource") ?? "listings";
  const ownerId = watch("ownerId");
  const ownershipType = watch("ownershipType");
  const area = watch("area") ?? "";
  const address = watch("address") ?? "";
  const city = watch("city") ?? "";
  const district = watch("district") ?? "";
  const postalCode = watch("postalCode") ?? "";
  const compoundName = watch("compoundName") ?? "";
  const compoundCode = watch("compoundCode") ?? "";
  const latitude = watch("latitude") ?? null;
  const longitude = watch("longitude") ?? null;
  const propertyImages = watch("images");
  const floorPlanImages = watch("floorPlanImages");
  const information = watch("information");
  const selectedUtilities = watch("utilities") as string[];
  const selectedHighlights = watch("highlights") as Record<string, string[]>;
  const legalValues = watch("legal");
  // Unit tab fields
  const unitPurpose = watch("unitPurpose") ?? "";
  const unitType = watch("unitType") ?? "";
  const unitName = watch("unitName") ?? "";
  const unitArea = watch("unitArea") ?? "";
  const unitImages = watch("unitImages");
  const annualRent = watch("annualRent") ?? "";
  const deposit = watch("deposit") ?? "";
  const saleAmount = watch("saleAmount") ?? "";
  const floorNumber = watch("floorNumber") ?? "";
  const isMezzanine = watch("isMezzanine");
  const frontSideLength = watch("frontSideLength") ?? "";
  const finishing = watch("finishing");
  const furnishingStatus = watch("furnishingStatus");
  const bedrooms = watch("bedrooms");
  const bathrooms = watch("bathrooms");
  const majles = watch("majles");
  const kitchens = watch("kitchens");
  const maidRooms = watch("maidRooms");
  const storageRooms = watch("storageRooms");
  const outdoorAreas = watch("outdoorAreas");
  const unitElectricityAccount = watch("unitElectricityAccount") ?? "";
  const unitWaterAccount = watch("unitWaterAccount") ?? "";
  const unitGasMeter = watch("unitGasMeter") ?? "";
  const unitCentralAC = watch("unitCentralAC");
  const unitSplitAC = watch("unitSplitAC");
  const unitDesertCooler = watch("unitDesertCooler");
  const unitWindowAC = watch("unitWindowAC");
  const unitParkingCount = watch("unitParkingCount");
  const unitHasHeating = watch("unitHasHeating");
  const unitHasKitchenCabinets = watch("unitHasKitchenCabinets");
  const unitHasKitchenSink = watch("unitHasKitchenSink");
  const unitHasDriversRoom = watch("unitHasDriversRoom");
  const unitIsSmartHome = watch("unitIsSmartHome");
  const unitHasBalcony = watch("unitHasBalcony");
  const unitHasParkingInside = watch("unitHasParkingInside");
  const unitHasParkingOutside = watch("unitHasParkingOutside");
  const unitLegalOwnershipType = watch("unitLegalOwnershipType") ?? "";
  const unitOwnershipNumber = watch("unitOwnershipNumber") ?? "";
  const unitHasInfoRestriction = watch("unitHasInfoRestriction");
  const unitInfoRestrictionText = watch("unitInfoRestrictionText") ?? "";
  const unitHasMortgageRestriction = watch("unitHasMortgageRestriction");
  const unitMortgageRestrictionText = watch("unitMortgageRestrictionText") ?? "";
  const unitHasObligations = watch("unitHasObligations");
  const unitObligationsText = watch("unitObligationsText") ?? "";
  const unitHasPropertyDispute = watch("unitHasPropertyDispute");
  const unitPropertyDisputeText = watch("unitPropertyDisputeText") ?? "";
  const unitOwnershipDocuments = watch("unitOwnershipDocuments");
  const unitOwnerId = watch("unitOwnerId") ?? null;

  // Legal date needs special handling (DatePicker returns Date object, form stores string)
  const [ownershipDateObj, setOwnershipDateObj] = useState<Date | undefined>();
  const [unitOwnershipDateObj, setUnitOwnershipDateObj] = useState<Date | undefined>();
  // F11: Hijri deed date (property level)
  const [ownershipDateHijri, setOwnershipDateHijri] = useState("");
  // F11: Hijri deed date (unit level)
  const [unitOwnershipDateHijri, setUnitOwnershipDateHijri] = useState("");

  // ── UI state (tab switching, pending units, modals) ─────────────────────────
  const [activeMainTab, setActiveMainTab] = useState<"property" | "unit">("property");
  const [activeInfoTab, setActiveInfoTab] = useState("");
  const [unitActiveInfoTab, setUnitActiveInfoTab] = useState("");
  const [pendingUnits, setPendingUnits] = useState<PendingUnit[]>([]);
  const [editingUnitIndex, setEditingUnitIndex] = useState<number | null>(null);
  const [showAddOwnerDialog, setShowAddOwnerDialog] = useState(false);
  // Which picker opened the Add-Owner dialog, so the newly-created owner is
  // applied to the right field (property owner vs. multi-owner unit owner).
  const [addOwnerTarget, setAddOwnerTarget] = useState<"property" | "unit">("property");

  // (Validation is handled via form.trigger() + RHF errors)

  // Unit-owner sync dialog (edit mode)
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncPendingOwnerId, setSyncPendingOwnerId] = useState<string | null>(null);
  const [syncPendingPayload, setSyncPendingPayload] = useState<Record<string, unknown> | null>(null);
  const [syncUnitCount, setSyncUnitCount] = useState(0);

  // Subscription limits
  const { checkUnitLimit, unitLimit } = useSubscriptionLimits();
  const { data: existingUnitCount = 0 } = useUnitUsage();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ current: number; limit: number; requested: number }>({ current: 0, limit: 0, requested: 0 });

  void existingUnitCount;
  void navigate;
  const isLimitReached = false;


  // ── Data constants ──────────────────────────────────────────────────────────
  const UTILITIES = [
    { id: "electricity", labelAr: "كهرباء", labelEn: "Electricity" },
    { id: "water", labelAr: "مياه", labelEn: "Water" },
    { id: "sewage", labelAr: "صرف صحي", labelEn: "Sewage" },
    { id: "fixed_phone", labelAr: "هاتف ثابت", labelEn: "Fixed Phone" },
    { id: "fiber_optics", labelAr: "ألياف بصرية", labelEn: "Fiber Optics" },
    { id: "flood_drainage", labelAr: "تصريف سيول", labelEn: "Flood Drainage" },
  ];

  const HIGHLIGHTS_CATEGORIES = [
    {
      id: "features",
      nameAr: "المميزات",
      nameEn: "Features",
      items: [
        { id: "parking", nameAr: "مواقف سيارات", nameEn: "Parking Spaces" },
        { id: "double_glazed", nameAr: "نوافذ مزدوجة الزجاج", nameEn: "Double Glazed Windows" },
      ],
    },
    {
      id: "building",
      nameAr: "المبنى",
      nameEn: "Building",
      items: [
        { id: "reception", nameAr: "استقبال / غرفة انتظار", nameEn: "Reception/Waiting Room" },
        { id: "prayer_room", nameAr: "مصلى", nameEn: "Prayer Room" },
        { id: "elevators", nameAr: "مصاعد في المبنى", nameEn: "Elevators in Building" },
        { id: "lobby", nameAr: "بهو في المبنى", nameEn: "Lobby in Building" },
        { id: "cafeteria", nameAr: "كافتيريا", nameEn: "Cafeteria" },
        { id: "grocery", nameAr: "بقالة", nameEn: "Grocery Store" },
        { id: "laundry", nameAr: "مغسلة", nameEn: "Laundry" },
      ],
    },
    {
      id: "healthFitness",
      nameAr: "الصحة واللياقة",
      nameEn: "Health and Fitness",
      items: [
        { id: "sauna", nameAr: "ساونا", nameEn: "Sauna" },
        { id: "gym", nameAr: "صالة رياضية", nameEn: "Gym Fitness Centers" },
        { id: "games_room", nameAr: "غرفة ألعاب", nameEn: "Games Room" },
        { id: "football", nameAr: "ملعب كرة قدم", nameEn: "Football Yard" },
        { id: "volleyball", nameAr: "ملعب كرة طائرة", nameEn: "Volleyball Yard" },
        { id: "tennis", nameAr: "ملعب تنس", nameEn: "Tennis Yard" },
        { id: "basketball", nameAr: "ملعب كرة سلة", nameEn: "Basketball Yard" },
        { id: "pool", nameAr: "مسبح", nameEn: "Swimming Pool" },
      ],
    },
    {
      id: "recreation",
      nameAr: "الترفيه والعائلة",
      nameEn: "Recreation and Family",
      items: [
        { id: "bbq", nameAr: "منطقة شواء", nameEn: "Barbeque Area" },
        { id: "kids_area", nameAr: "منطقة ألعاب أطفال", nameEn: "Kids Play Area" },
        { id: "garden", nameAr: "حديقة", nameEn: "Lawn or Garden" },
        { id: "banquet", nameAr: "قاعة مناسبات", nameEn: "Banquet Hall" },
        { id: "nursery", nameAr: "حضانة أطفال", nameEn: "Baby Nursery" },
      ],
    },
    {
      id: "cleaning",
      nameAr: "النظافة والصيانة",
      nameEn: "Cleaning and Maintenance",
      items: [
        { id: "waste", nameAr: "التخلص من النفايات", nameEn: "Waste Disposal" },
        { id: "maintenance_staff", nameAr: "فريق صيانة", nameEn: "Maintenance Staff" },
        { id: "cleaning_services", nameAr: "خدمات تنظيف", nameEn: "Cleaning Services" },
        { id: "transfer", nameAr: "خدمة نقل", nameEn: "Transfer Service" },
      ],
    },
    {
      id: "businessSecurity",
      nameAr: "الأعمال والأمن",
      nameEn: "Business and Security",
      items: [
        { id: "business_center", nameAr: "مركز أعمال", nameEn: "Business Center" },
        { id: "cctv", nameAr: "كاميرات مراقبة", nameEn: "CCTV Security" },
        { id: "security", nameAr: "خدمة أمن", nameEn: "Security Service" },
      ],
    },
    {
      id: "technology",
      nameAr: "التكنولوجيا",
      nameEn: "Technology",
      items: [
        { id: "internet", nameAr: "إنترنت عريض النطاق", nameEn: "Broadband Internet" },
        { id: "satellite", nameAr: "قنوات فضائية / كابل", nameEn: "Satellite/Cable TV" },
        { id: "intercom", nameAr: "اتصال داخلي", nameEn: "Intercom" },
      ],
    },
    {
      id: "miscellaneous",
      nameAr: "متنوعة",
      nameEn: "Miscellaneous",
      items: [
        { id: "view", nameAr: "إطلالة", nameEn: "View" },
        { id: "floor", nameAr: "طابق", nameEn: "Floor" },
      ],
    },
  ];

  // ── Derived values ──────────────────────────────────────────────────────────
  const availablePropertyTypes = useMemo(() => {
    return propertyPurpose ? getPropertyTypesForPurpose(propertyPurpose) : [];
  }, [propertyPurpose]);

  const availableUnitTypes = useMemo(() => {
    const purpose = unitPurpose || propertyPurpose;
    return resolveAvailableUnitTypes(purpose, propertyType);
  }, [unitPurpose, propertyPurpose, propertyType]);

  const isSingleUnit = propertyType ? isSingleUnitProperty(propertyType) : false;
  void isSingleUnit;

  // ── Dirty detection ─────────────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (isEditMode) return false;
    return !!(
      propertyName || propertyPurpose || propertyType || area || address ||
      propertyImages.length || floorPlanImages.length ||
      information?.shortAddress || information?.region || city || district ||
      unitName || unitType || unitArea || unitImages.length ||
      annualRent || deposit || saleAmount ||
      pendingUnits.length || (legalValues?.ownershipDocuments?.length ?? 0) > 0 ||
      legalValues?.ownershipNumber || legalValues?.ownershipType
    );
  }, [
    isEditMode, propertyName, propertyPurpose, propertyType, area, address,
    propertyImages.length, floorPlanImages.length,
    information?.shortAddress, information?.region, city, district,
    unitName, unitType, unitArea, unitImages.length,
    annualRent, deposit, saleAmount,
    pendingUnits.length, legalValues?.ownershipDocuments?.length,
    legalValues?.ownershipNumber, legalValues?.ownershipType,
  ]);
  const blocker = useFormGuard(open && isDirty);

  // ── Utility helpers ─────────────────────────────────────────────────────────
  const toggleUtility = (utilityId: string) => {
    const current = selectedUtilities as string[];
    const next = current.includes(utilityId)
      ? current.filter((id) => id !== utilityId)
      : [...current, utilityId];
    setValue("utilities", next);
  };

  // ── Effects ─────────────────────────────────────────────────────────────────

  // Auto-fill shortAddress from Nafath when dialog opens
  useEffect(() => {
    if (open && nafathIdentity?.address?.shortAddress) {
      const current = form.getValues("information.shortAddress");
      if (!current) {
        setValue("information.shortAddress", nafathIdentity.address.shortAddress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nafathIdentity?.address?.shortAddress]);

  // Pre-fill form when editing a property
  useEffect(() => {
    if (open && editProperty) {
      setValue("purpose", editProperty.purpose || "residential");
      setValue("propertyType", editProperty.property_type || "");
      setValue("name", editProperty.name || "");
      setValue("nameEn", editProperty.name_en || "");
      setValue("description", editProperty.description || "");
      setValue("descriptionEn", editProperty.description_en || "");
      setValue(
        "publicUnitsSource",
        editProperty.public_units_source === "vacant_units" ? "vacant_units" : "listings",
      );
      setValue("ownerId", editProperty.owner_id ?? null);
      setValue("ownershipType", editProperty.owner_id ? "single" : "multiple");
      setValue("area", editProperty.total_area?.toString() || "");
      setValue("address", editProperty.address || "");
      setValue("city", editProperty.city || "");
      setValue("district", editProperty.district || "");
      setValue("latitude", editProperty.latitude ?? null);
      setValue("longitude", editProperty.longitude ?? null);
      setValue("postalCode", editProperty.postal_code || "");
      // Compound metadata — optional, present only on rows created post-
      // 20260514150000 migration. Cast via unknown because the older
      // PropertyRow type from the generated types.ts doesn't include
      // these columns until it's regenerated.
      const compoundFields = editProperty as unknown as {
        compound_name?: string | null;
        compound_code?: string | null;
      };
      setValue("compoundName", compoundFields.compound_name || "");
      setValue("compoundCode", compoundFields.compound_code || "");
      setValue("information.floorsCount", editProperty.floors_count ?? 1);

      // Parse images
      const imgs = editProperty.images;
      if (Array.isArray(imgs)) {
        setValue("images", imgs.filter((u): u is string => typeof u === "string"));
      }

      // Parse amenities
      const amenities = editProperty.amenities as Record<string, unknown> | null;
      if (amenities) {
        if (Array.isArray(amenities.utilities)) {
          setValue("utilities", amenities.utilities as string[]);
        }
        if (amenities.highlights && typeof amenities.highlights === "object") {
          setValue("highlights", amenities.highlights as Record<string, string[]>);
        }
        const legal = amenities.legal as Record<string, unknown> | undefined;
        if (legal) {
          if (legal.ownershipType) setValue("legal.ownershipType", legal.ownershipType as string);
          if (legal.ownershipNumber) setValue("legal.ownershipNumber", legal.ownershipNumber as string);
          if (legal.ownershipDate) {
            setValue("legal.ownershipDate", legal.ownershipDate as string);
            setOwnershipDateObj(new Date(legal.ownershipDate as string));
          }
          // F11: load Hijri date
          if (typeof legal.ownershipDateHijri === "string") setOwnershipDateHijri(legal.ownershipDateHijri);
          if (legal.infoRestriction) setValue("legal.infoRestriction", legal.infoRestriction as string);
          if (legal.mortgageRestriction) setValue("legal.mortgageRestriction", legal.mortgageRestriction as string);
          if (legal.obligations) setValue("legal.obligations", legal.obligations as string);
          if (legal.propertyDispute) setValue("legal.propertyDispute", legal.propertyDispute as string);
          if (Array.isArray(legal.ownershipDocuments)) setValue("legal.ownershipDocuments", legal.ownershipDocuments as string[]);
        }
        const info = amenities.information as Record<string, unknown> | undefined;
        if (info) {
          if (info.shortAddress) setValue("information.shortAddress", info.shortAddress as string);
          if (info.nationalAddress) setValue("information.nationalAddress", info.nationalAddress as string);
          if (info.country) setValue("information.country", info.country as string);
          if (info.region) setValue("information.region", info.region as string);
          if (info.streetName) setValue("information.streetName", info.streetName as string);
          if (info.buildingNumber) setValue("information.buildingNumber", info.buildingNumber as string);
          if (info.secondaryNumber) setValue("information.secondaryNumber", info.secondaryNumber as string);
          if (info.streetWidth) setValue("information.streetWidth", info.streetWidth as string);
          if (info.landNumber) setValue("information.landNumber", info.landNumber as string);
          if (info.planNumber) setValue("information.planNumber", info.planNumber as string);
          if (info.propertyAge) setValue("information.propertyAge", info.propertyAge as string);
          if (info.direction) setValue("information.direction", info.direction as string);
          if (info.propertyValue) setValue("information.propertyValue", info.propertyValue as string);
          if (info.communityName) setValue("information.communityName", info.communityName as string);
          // `!= null` (not truthy) so a saved 0 loads back as 0, not the default 1.
          if (info.unitsPerFloor != null) setValue("information.unitsPerFloor", info.unitsPerFloor as number);
          if (info.parkingLots != null) setValue("information.parkingLots", info.parkingLots as number);
          if (info.elevators != null) setValue("information.elevators", info.elevators as number);
          if (Array.isArray(info.floorPlanImages)) setValue("floorPlanImages", info.floorPlanImages as string[]);
        }
      }

      setActiveMainTab("property");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editProperty]);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const resetUnitFields = () => {
    const unitDefaults: Partial<PropertyFormValues> = {
      unitType: availableUnitTypes[0] || "",
      unitName: "",
      unitArea: "",
      unitImages: [],
      annualRent: "",
      deposit: "",
      saleAmount: "",
      floorNumber: "",
      isMezzanine: false,
      frontSideLength: "",
      finishing: "finished",
      furnishingStatus: "unfurnished",
      bedrooms: 1,
      bathrooms: 1,
      majles: 1,
      kitchens: 1,
      maidRooms: 0,
      storageRooms: 0,
      outdoorAreas: 0,
      unitElectricityAccount: "",
      unitWaterAccount: "",
      unitGasMeter: "",
      unitCentralAC: 0,
      unitSplitAC: 0,
      unitDesertCooler: 0,
      unitWindowAC: 0,
      unitParkingCount: 0,
      unitHasHeating: false,
      unitHasKitchenCabinets: false,
      unitHasKitchenSink: false,
      unitHasDriversRoom: false,
      unitIsSmartHome: false,
      unitHasBalcony: false,
      unitHasParkingInside: false,
      unitHasParkingOutside: false,
      unitLegalOwnershipType: "",
      unitOwnershipNumber: "",
      unitOwnershipDate: "",
      unitHasInfoRestriction: false,
      unitInfoRestrictionText: "",
      unitHasMortgageRestriction: false,
      unitMortgageRestrictionText: "",
      unitHasObligations: false,
      unitObligationsText: "",
      unitHasPropertyDispute: false,
      unitPropertyDisputeText: "",
      unitOwnershipDocuments: [],
      unitOwnerId: null,
    };
    // Batch all unit field resets in one pass — keepValues: true is not available on reset,
    // so we merge with current property-level values to preserve them.
    reset({ ...form.getValues(), ...unitDefaults }, { keepErrors: false });
    setUnitOwnershipDateObj(undefined);
    setUnitOwnershipDateHijri("");
    setUnitActiveInfoTab("information");
  };

  const resetForm = () => {
    reset();
    setOwnershipDateObj(undefined);
    setOwnershipDateHijri("");
    setUnitOwnershipDateObj(undefined);
    setUnitOwnershipDateHijri("");
    setPendingUnits([]);
    setEditingUnitIndex(null);
    setActiveMainTab("property");
    setActiveInfoTab("information");
    setUnitActiveInfoTab("information");
  };

  const collectCurrentUnitData = (): PendingUnit => ({
    // Use the form's unitPurpose (NOT propertyPurpose) so multi_purpose
    // properties correctly persist the per-unit choice (residential vs
    // commercial vs …). Falls back to propertyPurpose for single-purpose
    // properties where the unit field is locked to match.
    unitPurpose: unitPurpose || propertyPurpose,
    unitType,
    unitName,
    unitArea,
    unitImages,
    annualRent,
    deposit,
    saleAmount,
    floorNumber,
    isMezzanine,
    frontSideLength,
    finishing,
    furnishingStatus,
    bedrooms,
    bathrooms,
    majles,
    kitchens,
    maidRooms,
    storageRooms,
    outdoorAreas,
    utilities: {
      electricity: unitElectricityAccount,
      water: unitWaterAccount,
      gas: unitGasMeter,
    },
    highlights: {
      counters: {
        centralAC: unitCentralAC,
        splitAC: unitSplitAC,
        desertCooler: unitDesertCooler,
        windowAC: unitWindowAC,
        parking: unitParkingCount,
      },
      checkboxes: {
        heating: unitHasHeating,
        kitchenCabinets: unitHasKitchenCabinets,
        kitchenSink: unitHasKitchenSink,
        driversRoom: unitHasDriversRoom,
        smartHome: unitIsSmartHome,
        balcony: unitHasBalcony,
        parkingInside: unitHasParkingInside,
        parkingOutside: unitHasParkingOutside,
      },
    },
    owner_id: unitOwnerId || undefined,
    ...(ownershipType === "multiple" ? {
      legal: {
        ownershipType: unitLegalOwnershipType,
        ownershipNumber: unitOwnershipNumber,
        ownershipDate: unitOwnershipDateObj?.toISOString(),
        // F11: Hijri date (unit level in AddPropertyDialog)
        ownershipDateHijri: unitOwnershipDateHijri || undefined,
        infoRestriction: unitHasInfoRestriction ? unitInfoRestrictionText : null,
        mortgageRestriction: unitHasMortgageRestriction ? unitMortgageRestrictionText : null,
        obligations: unitHasObligations ? unitObligationsText : null,
        propertyDispute: unitHasPropertyDispute ? unitPropertyDisputeText : null,
        ownershipDocuments: unitOwnershipDocuments,
      },
    } : {}),
  });

  const loadUnitForEdit = (index: number) => {
    const unit = pendingUnits[index];
    setValue("unitPurpose", unit.unitPurpose);
    setValue("unitType", unit.unitType);
    setValue("unitName", unit.unitName);
    setValue("unitArea", unit.unitArea);
    setValue("unitImages", unit.unitImages);
    setValue("annualRent", unit.annualRent);
    setValue("deposit", unit.deposit);
    setValue("saleAmount", unit.saleAmount);
    setValue("floorNumber", unit.floorNumber);
    setValue("isMezzanine", unit.isMezzanine);
    setValue("frontSideLength", unit.frontSideLength);
    setValue("finishing", unit.finishing);
    setValue("furnishingStatus", unit.furnishingStatus);
    setValue("bedrooms", unit.bedrooms);
    setValue("bathrooms", unit.bathrooms);
    setValue("majles", unit.majles);
    setValue("kitchens", unit.kitchens);
    setValue("maidRooms", unit.maidRooms);
    setValue("storageRooms", unit.storageRooms);
    setValue("outdoorAreas", unit.outdoorAreas);
    setValue("unitElectricityAccount", unit.utilities.electricity);
    setValue("unitWaterAccount", unit.utilities.water);
    setValue("unitGasMeter", unit.utilities.gas);
    setValue("unitCentralAC", unit.highlights.counters.centralAC ?? 0);
    setValue("unitSplitAC", unit.highlights.counters.splitAC ?? 0);
    setValue("unitDesertCooler", unit.highlights.counters.desertCooler ?? 0);
    setValue("unitWindowAC", unit.highlights.counters.windowAC ?? 0);
    setValue("unitParkingCount", unit.highlights.counters.parking ?? 0);
    setValue("unitHasHeating", unit.highlights.checkboxes.heating ?? false);
    setValue("unitHasKitchenCabinets", unit.highlights.checkboxes.kitchenCabinets ?? false);
    setValue("unitHasKitchenSink", unit.highlights.checkboxes.kitchenSink ?? false);
    setValue("unitHasDriversRoom", unit.highlights.checkboxes.driversRoom ?? false);
    setValue("unitIsSmartHome", unit.highlights.checkboxes.smartHome ?? false);
    setValue("unitHasBalcony", unit.highlights.checkboxes.balcony ?? false);
    setValue("unitHasParkingInside", unit.highlights.checkboxes.parkingInside ?? false);
    setValue("unitHasParkingOutside", unit.highlights.checkboxes.parkingOutside ?? false);
    if (unit.owner_id) setValue("unitOwnerId", unit.owner_id);
    if (unit.legal) {
      setValue("unitLegalOwnershipType", unit.legal.ownershipType);
      setValue("unitOwnershipNumber", unit.legal.ownershipNumber);
      setValue("unitOwnershipDate", unit.legal.ownershipDate || "");
      // F11: load Hijri date when editing pending unit
      setUnitOwnershipDateHijri(unit.legal.ownershipDateHijri || "");
      setValue("unitHasInfoRestriction", !!unit.legal.infoRestriction);
      setValue("unitInfoRestrictionText", unit.legal.infoRestriction || "");
      setValue("unitHasMortgageRestriction", !!unit.legal.mortgageRestriction);
      setValue("unitMortgageRestrictionText", unit.legal.mortgageRestriction || "");
      setValue("unitHasObligations", !!unit.legal.obligations);
      setValue("unitObligationsText", unit.legal.obligations || "");
      setValue("unitHasPropertyDispute", !!unit.legal.propertyDispute);
      setValue("unitPropertyDisputeText", unit.legal.propertyDispute || "");
      setValue("unitOwnershipDocuments", unit.legal.ownershipDocuments);
    }
    setEditingUnitIndex(index);
  };

  const handleAddUnit = async () => {
    const valid = await form.trigger(["unitType", "unitName", ...(ownershipType === "multiple" ? ["unitOwnerId"] as const : [])]);
    if (!valid) return;
    if (isLimitReached) {
      setShowUpgradeModal(true);
      return;
    }
    const unitData = collectCurrentUnitData();
    if (editingUnitIndex !== null) {
      setPendingUnits((prev) => prev.map((u, i) => i === editingUnitIndex ? unitData : u));
      setEditingUnitIndex(null);
      toast.success(t("تم تحديث الوحدة", "Unit updated"));
    } else {
      setPendingUnits((prev) => [...prev, unitData]);
      toast.success(t("تم إضافة الوحدة", "Unit added"));
    }
    resetUnitFields();
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleDialogOpenChange = (isOpen: boolean) => {
    if (!isOpen) handleClose();
  };

  const handlePurposeChange = (purpose: string) => {
    setValue("purpose", purpose);
    // Single-purpose property → unit inherits and is locked to that purpose.
    // multi_purpose → clear unit purpose so the user must pick per-unit (the
    // field is enabled below; defaulting to "multi_purpose" would be invalid
    // since a unit can only have one concrete purpose).
    const nextUnitPurpose = purpose === "multi_purpose" ? "" : purpose;
    setValue("unitPurpose", nextUnitPurpose);

    // Auto-chain downstream dropdowns. Without this, the user lands on the
    // Unit Info tab with both Property Type and Unit Type empty (because the
    // previous selections are scoped to the previous purpose), which looks
    // like the form is broken.
    const nextPropertyType = getPropertyTypesForPurpose(purpose)[0] ?? "";
    setValue("propertyType", nextPropertyType);

    if (nextPropertyType) {
      // For multi_purpose properties unit purpose is left blank for the user
      // to pick — until they do, fall back to "residential" so we can still
      // populate a sensible default unit type instead of an empty dropdown.
      const matrixPurpose = nextUnitPurpose || "residential";
      const unitTypes = resolveAvailableUnitTypes(matrixPurpose, nextPropertyType);
      setValue("unitType", unitTypes[0] ?? "");
    } else {
      setValue("unitType", "");
    }
  };

  const handlePropertyTypeChange = (type: string) => {
    setValue("propertyType", type);
    // Use the unit purpose if it's been chosen explicitly (multi_purpose case),
    // otherwise fall back to the property purpose. Also use the matrix-with-
    // purpose-fallback resolver so medical/industrial/agricultural sub-purposes
    // inside multi_purpose properties still resolve to a non-empty list.
    const matrixPurpose = unitPurpose || propertyPurpose;
    const unitTypes = resolveAvailableUnitTypes(matrixPurpose, type);
    if (unitTypes.length > 0) {
      setValue("unitType", unitTypes[0]);
    } else {
      setValue("unitType", "");
    }
  };

  const handlePlaceSelect = useCallback((data: PlaceAddressData) => {
    if (data.region) setValue("information.region", data.region);
    if (data.city) setValue("city", data.city);
    if (data.district) setValue("district", data.district);
    if (data.streetName) setValue("information.streetName", data.streetName);
    if (data.buildingNumber) setValue("information.buildingNumber", data.buildingNumber);
    if (data.postalCode) setValue("postalCode", data.postalCode);
    if (data.latitude != null) setValue("latitude", data.latitude);
    if (data.longitude != null) setValue("longitude", data.longitude);
    if (data.formattedAddress) setValue("address", data.formattedAddress);
  }, [setValue]);

  const handleNext = async () => {
    const valid = await form.trigger([
      "purpose",
      "propertyType",
      "name",
      "ownerId",
      "address",
      "legal.ownershipType",
      "legal.ownershipNumber",
      "legal.ownershipDate",
    ]);
    if (!valid) {
      const legalErr = form.formState.errors.legal;
      if (legalErr?.ownershipType || legalErr?.ownershipNumber || legalErr?.ownershipDate) {
        setActiveInfoTab("legal");
        toast.error(t(
          "يرجى تعبئة معلومات الملكية المطلوبة في القسم القانوني",
          "Please fill in the required ownership fields in the Legal section",
        ));
      } else {
        // Fail-loud fallback (issue #2892): a rejection on purpose/propertyType/
        // name/ownerId/address previously produced NO toast and NO tab switch —
        // the user (and every E2E journey) was left staring at an unchanged Tab
        // 1 with zero feedback about what was wrong. Most commonly this is the
        // address field: on envs with a Google Maps key, AddressSearchField's
        // Places widget can silently fail to fire `place_changed` for a click
        // that lands on a `.pac-item` suggestion (a known Google Autocomplete
        // flakiness class, not unique to automated testing), leaving `address`
        // empty in the form even though the input visibly shows typed text.
        toast.error(t(
          "يرجى تعبئة جميع الحقول المطلوبة في معلومات العقار",
          "Please fill in all required fields in Property Info",
        ));
      }
      return;
    }
    if (ownershipType === "single" && !ownerId) {
      toast.error(t("يجب تحديد مالك العقار", "Please select a property owner"));
      return;
    }
    setActiveMainTab("unit");
  };

  const buildAmenitiesData = () => ({
    utilities: selectedUtilities,
    highlights: selectedHighlights,
    legal: {
      ownershipType: legalValues?.ownershipType || undefined,
      ownershipNumber: legalValues?.ownershipNumber || undefined,
      ownershipDate: ownershipDateObj?.toISOString(),
      // F11: Hijri date (property level)
      ownershipDateHijri: ownershipDateHijri || undefined,
      infoRestriction: legalValues?.infoRestriction || null,
      mortgageRestriction: legalValues?.mortgageRestriction || null,
      obligations: legalValues?.obligations || null,
      propertyDispute: legalValues?.propertyDispute || null,
      ownershipDocuments: (legalValues?.ownershipDocuments?.length ?? 0) > 0 ? legalValues?.ownershipDocuments : undefined,
    },
    information: {
      shortAddress: information?.shortAddress || undefined,
      nationalAddress: information?.nationalAddress || undefined,
      country: information?.country || undefined,
      region: information?.region || undefined,
      streetName: information?.streetName || undefined,
      buildingNumber: information?.buildingNumber || undefined,
      secondaryNumber: information?.secondaryNumber || undefined,
      streetWidth: information?.streetWidth || undefined,
      landNumber: information?.landNumber || undefined,
      planNumber: information?.planNumber || undefined,
      propertyAge: information?.propertyAge || undefined,
      direction: information?.direction || undefined,
      propertyValue: information?.propertyValue || undefined,
      communityName: information?.communityName || undefined,
      unitsPerFloor: information?.unitsPerFloor,
      parkingLots: information?.parkingLots,
      elevators: information?.elevators,
      floorPlanImages: (floorPlanImages?.length ?? 0) > 0 ? floorPlanImages : undefined,
    },
  });

  const handleSubmit = async () => {
    if (authLoading) {
      toast.info(t("جاري تحميل بيانات الحساب...", "Loading account data..."));
      return;
    }
    if (!companyId) {
      toast.error(t(
        "لا يمكن إضافة عقار لأن حسابك غير مرتبط بشركة.",
        "Can't add a property because your account isn't linked to a company."
      ));
      return;
    }
    const valid = await form.trigger([
      "purpose",
      "propertyType",
      "name",
      "ownerId",
      "address",
      "legal.ownershipType",
      "legal.ownershipNumber",
      "legal.ownershipDate",
    ]);
    if (!valid) {
      const legalErr = form.formState.errors.legal;
      if (legalErr?.ownershipType || legalErr?.ownershipNumber || legalErr?.ownershipDate) {
        setActiveInfoTab("legal");
        toast.error(t(
          "يرجى تعبئة معلومات الملكية المطلوبة في القسم القانوني",
          "Please fill in the required ownership fields in the Legal section",
        ));
      } else {
        // Fail-loud fallback — see the matching comment in handleNext (#2892).
        toast.error(t(
          "يرجى تعبئة جميع الحقول المطلوبة في معلومات العقار",
          "Please fill in all required fields in Property Info",
        ));
      }
      return;
    }
    if (ownershipType === "single" && !ownerId) {
      toast.error(t("يجب تحديد مالك العقار", "Please select a property owner"));
      return;
    }

    try {
      const amenitiesData = buildAmenitiesData();

      const propertyPayload = {
        name: propertyName,
        name_en: nameEn || null,
        description: description.trim() || null,
        description_en: descriptionEn.trim() || null,
        public_units_source: publicUnitsSource,
        property_type: propertyType,
        purpose: propertyPurpose,
        address,
        city,
        district,
        postal_code: postalCode,
        // Empty form fields → null in the DB so they don't pollute the
        // compound display section in the task context card.
        compound_name: compoundName.trim() || null,
        compound_code: compoundCode.trim() || null,
        total_area: area ? parseFloat(area) : undefined,
        floors_count: information?.floorsCount,
        images: (propertyImages?.length ?? 0) > 0 ? propertyImages : undefined,
        amenities: amenitiesData,
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
        owner_id: ownershipType === "multiple" ? null : (ownerId ?? undefined),
      };

      if (isEditMode && editProperty) {
        // Ask about cascading the owner to all units ONLY when the ownership
        // TYPE actually changed (multiple → single). Just swapping the owner
        // value while staying on single is a per-property change — the units
        // still belong to whoever they belonged to (single-owner property =
        // owner inherited at query time), so no per-unit migration is needed
        // and the confirmation popup becomes noise.
        const originalOwnershipType = editProperty.owner_id ? "single" : "multiple";
        const ownershipTypeChanged = ownershipType !== originalOwnershipType;
        if (
          companyId &&
          ownershipType === "single" &&
          ownerId &&
          ownershipTypeChanged
        ) {
          const count = await unitsRepository.countActiveForProperty(
            editProperty.id,
            companyId,
          );

          if ((count ?? 0) > 0) {
            // Store payload and show dialog — save happens in dialog callbacks
            setSyncPendingPayload({ id: editProperty.id, ...propertyPayload });
            setSyncPendingOwnerId(ownerId);
            setSyncUnitCount(count ?? 0);
            setShowSyncDialog(true);
            return;
          }
        }

        // No sync needed — save directly
        await updateProperty.mutateAsync({ id: editProperty.id, ...propertyPayload });
        await cascadeUnitPurposesIfNeeded(editProperty, propertyPurpose, companyId, queryClient);
        // Propagate the new owner to draft/pending contracts so the contract
        // list reflects the current owner immediately. Only runs when the owner
        // FK actually changed; active/terminated contracts are untouched.
        // Single-owner by design: multiple-owner properties carry per-UNIT owners
        // (units.owner_id), not a property-level owner, so there is no property
        // owner to propagate — the unit sync path above handles those.
        if (ownershipType === "single" && ownerId && companyId) {
          await propagateDraftContractOwnerIfNeeded(
            editProperty.id,
            ownerId,
            editProperty.owner_id,
            companyId,
            queryClient,
          );
        }
        toast.success(t("تم تحديث العقار بنجاح", "Property updated successfully"));
        handleClose();
        return;
      }

      // Create mode: require units only when user has unit permissions
      const unitsToCreate = [...pendingUnits];
      if (unitName.trim() && unitType) {
        unitsToCreate.push(collectCurrentUnitData());
      }

      if (canCreateUnits && unitsToCreate.length === 0) {
        toast.error(t("يرجى إضافة وحدة واحدة على الأقل", "Please add at least one unit"));
        return;
      }

      if (unitsToCreate.length > 0) {
        const limitCheck = await checkUnitLimit(unitsToCreate.length);
        if (!limitCheck.canAdd) {
          setLimitInfo({
            current: limitCheck.current,
            limit: limitCheck.limit,
            requested: unitsToCreate.length,
          });
          setShowUpgradeModal(true);
          return;
        }
      }

      const newProperty = await createProperty.mutateAsync(propertyPayload);

      for (const unitData of unitsToCreate) {
        const unitAmenities = {
          utilities: unitData.utilities,
          highlights: unitData.highlights,
          legal: unitData.legal,
        };

        await createUnit.mutateAsync({
          property_id: newProperty.id,
          unit_number: unitData.unitName || `Unit ${unitsToCreate.indexOf(unitData) + 1}`,
          unit_type: unitData.unitType as Database["public"]["Enums"]["unit_type"],
          purpose: unitData.unitPurpose,
          area: unitData.unitArea ? parseFloat(unitData.unitArea) : undefined,
          rent_amount: unitData.annualRent ? parseFloat(unitData.annualRent) : undefined,
          annual_rent: unitData.annualRent ? parseFloat(unitData.annualRent) : undefined,
          deposit_amount: unitData.deposit ? parseFloat(unitData.deposit) : undefined,
          sale_price: unitData.saleAmount ? parseFloat(unitData.saleAmount) : undefined,
          floor_number: unitData.floorNumber ? parseInt(unitData.floorNumber) : undefined,
          is_furnished: unitData.furnishingStatus === "new" || unitData.furnishingStatus === "used",
          bedrooms: unitData.bedrooms,
          bathrooms: unitData.bathrooms,
          living_rooms: unitData.majles,
          electricity_meter: unitData.utilities.electricity || undefined,
          water_meter: unitData.utilities.water || undefined,
          status: "available",
          images: unitData.unitImages.length > 0 ? unitData.unitImages : undefined,
          amenities: unitAmenities,
          owner_id: ownershipType === "single" ? (ownerId ?? undefined) : unitData.owner_id,
        });
      }

      toast.success(
        unitsToCreate.length > 0
          ? t("تم إضافة العقار والوحدات بنجاح", "Property and units added successfully")
          : t("تم إضافة العقار بنجاح", "Property added successfully")
      );
      handleClose();
    } catch (error) {
      toast.error(t(
        isEditMode ? "حدث خطأ أثناء تحديث العقار" : "حدث خطأ أثناء إضافة العقار",
        isEditMode ? "Error updating property" : "Error adding property"
      ));
    }
  };

  const isSubmitting = createProperty.isPending || createUnit.isPending || updateProperty.isPending;
  const canProceed = !isSubmitting && !authLoading && !!companyId;

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] p-0 flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="property-form"
      >
        <DialogHeader className="p-4 pb-0 shrink-0">
          <DialogTitle className="text-center text-lg font-bold">
            {isEditMode ? t("تعديل العقار", "Edit Property") : t("إضافة عقار", "Add Property")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEditMode ? t("نموذج لتعديل العقار", "Form to edit property") : t("نموذج لإضافة عقار جديد", "Form to add a new property")}
          </DialogDescription>
        </DialogHeader>

        {!authLoading && !companyId && (
          <div className="mx-4 mt-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {t(
              "حسابك غير مرتبط بشركة حالياً.",
              "Your account isn't linked to a company yet."
            )}
          </div>
        )}

        {/* Main Tabs: Property Info / Unit Info */}
        <Tabs value={activeMainTab} onValueChange={(v) => setActiveMainTab(v as "property" | "unit")} className="w-full flex-1 flex flex-col min-h-0">
          {!isEditMode && (
          <TabsList className="w-full justify-center bg-transparent border-b rounded-none h-auto p-0 mx-4 shrink-0" style={{ width: 'calc(100% - 32px)' }}>
            <TabsTrigger
              value="property"
              className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary rounded-none pb-2 text-sm"
            >
              {t("معلومات العقار", "Property Info")}
            </TabsTrigger>
            <TabsTrigger
              value="unit"
              className="flex-1 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary rounded-none pb-2 text-sm"
            >
              {t("معلومات الوحدة", "Unit Info")}
            </TabsTrigger>
          </TabsList>
          )}

          {/* Plain overflow-y-auto scroller (not the Radix ScrollArea) — the
              Radix viewport's h-full does not reliably resolve to a bounded
              height inside this flex+Tabs column, so the body never scrolled and
              the footer clipped. A flex-1 min-h-0 overflow-y-auto div is the
              proven pattern (see RoleManagementDialog). */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* PROPERTY INFO TAB */}
            <TabsContent value="property" className="p-4 mt-0 space-y-4">
              {/* Basic Info */}
              <div className="space-y-3">
                {/* Property Purpose */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("غرض العقار", "Property purpose")} <span className="text-destructive">*</span>
                  </Label>
                  <Select value={propertyPurpose} onValueChange={handlePurposeChange}>
                    <SelectTrigger data-testid="property-purpose-trigger" className={cn(errors.purpose && "border-destructive")}>
                      <SelectValue placeholder={t("اختر غرض العقار", "Select property purpose")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_PURPOSES.map((purpose) => (
                        <SelectItem key={purpose.value} value={purpose.value}>
                          {language === "ar" ? purpose.labelAr : purpose.labelEn}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.purpose && <p className="text-destructive text-xs mt-1">{t("غرض العقار مطلوب", "Purpose is required")}</p>}
                </div>

                {/* Property Type */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("نوع العقار", "Property type")} <span className="text-destructive">*</span>
                  </Label>
                  <Select value={propertyType} onValueChange={handlePropertyTypeChange} disabled={!propertyPurpose}>
                    <SelectTrigger data-testid="property-type-trigger" className={cn(errors.propertyType && "border-destructive")}>
                      <SelectValue placeholder={t("اختر نوع العقار", "Select property type")} />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePropertyTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {getPropertyTypeLabel(type, language as "ar" | "en")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.propertyType && <p className="text-destructive text-xs mt-1">{t("نوع العقار مطلوب", "Property type is required")}</p>}
                </div>

                {/* Property Name (Arabic) */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("اسم العقار", "Property name")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    data-testid="property-name-input"
                    placeholder={t("أدخل اسم العقار", "Enter property name")}
                    value={propertyName}
                    onChange={(e) => setValue("name", e.target.value)}
                    className={cn(errors.name && "border-destructive")}
                  />
                  {errors.name && <p className="text-destructive text-xs mt-1">{t("اسم العقار مطلوب", "Property name is required")}</p>}
                </div>

                {/* Property Name (English) */}
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("اسم العقار (إنجليزي)", "Property name (English)")}</Label>
                  <Input
                    data-testid="property-name-en-input"
                    placeholder="Enter property name in English"
                    value={nameEn}
                    onChange={(e) => setValue("nameEn", e.target.value)}
                  />
                </div>

                {/* Ownership Type */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("نوع الملكية", "Ownership type")} <span className="text-destructive">*</span>
                  </Label>
                  <RadioGroup
                    value={ownershipType}
                    onValueChange={(v) => {
                      setValue("ownershipType", v as "single" | "multiple");
                      if (v === "multiple") {
                        setValue("ownerId", null);
                        if (pendingUnits.length > 0 || isEditMode) {
                          toast.info(t(
                            "يرجى تعيين مالك لكل وحدة من الوحدات المضافة",
                            "Please assign an owner to each added unit"
                          ));
                        }
                      } else {
                        setValue("unitOwnerId", null);
                      }
                    }}
                    className="flex gap-6"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="single" id="single" />
                      <Label htmlFor="single" className="font-normal text-sm">{t("فردي", "Single")}</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="multiple" id="multiple" />
                      <Label htmlFor="multiple" className="font-normal text-sm">{t("متعدد", "Multiple")}</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Property Owner — only for single ownership */}
                {ownershipType === "single" && (
                  <div className="space-y-1.5">
                    <Label className="text-sm">
                      {t("مالك العقار", "Property owner")} <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex gap-2 items-stretch">
                      <div className="flex-1">
                        <SearchableSelect
                          items={owners}
                          value={ownerId}
                          triggerTestId="property-owner-trigger"
                          onValueChange={(v) => setValue("ownerId", v)}
                          getKey={(o) => o.id}
                          getLabel={(o) =>
                            getOwnerEntityDisplayName(o, ownerMeta[o.id], language)
                          }
                          getSubtitle={(o) => {
                            const meta = ownerMeta[o.id];
                            if (isOrganizationOwner(meta)) {
                              // The chip already says "Organization"; surface the
                              // representative (falls back to the contact person).
                              const rep =
                                getOwnerRepresentativeName(meta, language) ||
                                (language === "ar" ? o.full_name : o.full_name_en || o.full_name);
                              return rep ? `${t("الممثل", "Rep")}: ${rep}` : null;
                            }
                            return o.national_id || o.phone || null;
                          }}
                          getBadge={(o) => ownerTypeChip(ownerMeta[o.id])}
                          getSearchString={(o) => {
                            const meta = ownerMeta[o.id];
                            return `${o.full_name ?? ""} ${o.full_name_en ?? ""} ${o.national_id ?? ""} ${o.phone ?? ""} ${meta?.commercial_name ?? ""} ${meta?.commercial_name_en ?? ""} ${meta?.representative_name ?? ""}`;
                          }}
                          placeholder={t("اختر المالك", "Select property owner")}
                          searchPlaceholder={t("ابحث بالاسم، الهوية، الجوال", "Search by name, ID, phone")}
                          emptyText={t("لا يوجد مالك مطابق", "No matching owner")}
                          noneLabel={t("بدون مالك", "No owner")}
                          hasError={!!errors.ownerId}
                        />
                      </div>
                      {canCreateOwners && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 h-11"
                          onClick={() => { setAddOwnerTarget("property"); setShowAddOwnerDialog(true); }}
                        >
                          {t("إضافة مالك", "Add New Owner")}
                        </Button>
                      )}
                    </div>
                    {errors.ownerId && <p className="text-destructive text-xs mt-1">{t("مالك العقار مطلوب", "Property owner is required")}</p>}
                  </div>
                )}

                {/* Area */}
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("المساحة (م²)", "Area (Sq. M)")}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    placeholder={t("أدخل المساحة", "Enter area")}
                    value={area}
                    onChange={(e) => setValue("area", e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') e.preventDefault(); }}
                  />
                </div>

                {/* Address */}
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("العنوان", "Address")} <span className="text-destructive">*</span></Label>
                  <AddressSearchField
                    inputTestId="property-address-input"
                    onPlaceSelect={handlePlaceSelect}
                    placeholder={t("مثال: الرياض، حي المروج", "e.g. Riyadh, Al-Muruj District")}
                    label={null}
                    value={address}
                    error={!!errors.address}
                    onChange={(v) => setValue("address", v)}
                  />
                  {errors.address && <p className="text-destructive text-xs mt-1">{t("العنوان مطلوب", "Address is required")}</p>}
                  <MapPinPicker
                    latitude={latitude}
                    longitude={longitude}
                    onLocationChange={(lat, lng) => {
                      setValue("latitude", lat);
                      setValue("longitude", lng);
                    }}
                    onPlaceSelect={handlePlaceSelect}
                  />
                </div>

                {/* Property Images */}
                <ImageUpload
                  label={t("صور ووثائق العقار", "Property images and documents")}
                  folder="properties"
                  companyId={companyId}
                  value={propertyImages}
                  onChange={(v) => setValue("images", v)}
                  maxFiles={MAX_GALLERY_IMAGES}
                  accept={IMAGE_VIDEO_OR_PDF_ACCEPT}
                />
              </div>

              {/* Sub-tabs: Information, Utilities, Highlights, Legal */}
              <div className="pt-2">
                <div className="flex gap-2 flex-wrap justify-center">
                  {["information", "utilities", "highlights", "legal", "publicPage"].map((tab) => (
                    <Button
                      key={tab}
                      type="button"
                      variant={activeInfoTab === tab ? "default" : "outline"}
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => setActiveInfoTab(activeInfoTab === tab ? "" : tab)}
                    >
                      {activeInfoTab === tab ? (
                        <Minus className="h-3 w-3 me-1" />
                      ) : (
                        <Plus className="h-3 w-3 me-1" />
                      )}
                      {tab === "information" && t("المعلومات", "Information")}
                      {tab === "utilities" && t("المرافق", "Utilities")}
                      {tab === "highlights" && t("المميزات", "Highlights")}
                      {tab === "legal" && t("القانونية", "Legal")}
                      {tab === "publicPage" && t("الصفحة العامة", "Public Page")}
                    </Button>
                  ))}
                </div>

                {activeInfoTab === "information" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">{t("معلومات إضافية", "Additional Information")}</h4>

                    {/* National Address Section */}
                    <div className="space-y-3">
                      <h5 className="text-primary/80 font-medium text-xs">{t("العنوان الوطني", "National Address")}</h5>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("العنوان المختصر", "Short address")}</Label>
                        <Input
                          placeholder={t("أدخل العنوان المختصر", "Enter short address")}
                          value={information?.shortAddress ?? ""}
                          onChange={(e) => setValue("information.shortAddress", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("العنوان الوطني", "National address")}</Label>
                        <Input
                          placeholder={t("أدخل العنوان الوطني", "Enter national address")}
                          value={information?.nationalAddress ?? ""}
                          onChange={(e) => setValue("information.nationalAddress", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("الدولة", "Country")}</Label>
                        <Select value={information?.country ?? "SA"} onValueChange={(v) => setValue("information.country", v)}>
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SA">{t("المملكة العربية السعودية", "Saudi Arabia")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("المنطقة", "Region")}</Label>
                          <Input
                            placeholder={t("أدخل المنطقة", "Enter region")}
                            value={information?.region ?? ""}
                            onChange={(e) => setValue("information.region", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("المدينة", "City")}</Label>
                          <Input
                            placeholder={t("أدخل المدينة", "Enter city")}
                            value={city}
                            onChange={(e) => setValue("city", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("الحي", "District")}</Label>
                          <Input
                            placeholder={t("أدخل اسم الحي", "Enter district name")}
                            value={district}
                            onChange={(e) => setValue("district", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("اسم الشارع", "Street name")}</Label>
                          <Input
                            placeholder={t("أدخل اسم الشارع", "Enter street name")}
                            value={information?.streetName ?? ""}
                            onChange={(e) => setValue("information.streetName", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("رقم المبنى", "Building number")}</Label>
                          <Input
                            placeholder={t("مثال: 123", "e.g. 123")}
                            value={information?.buildingNumber ?? ""}
                            onChange={(e) => setValue("information.buildingNumber", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("الرمز البريدي", "Postal code")}</Label>
                          <Input
                            placeholder={t("مثال: 10345", "e.g. 10345")}
                            value={postalCode}
                            onChange={(e) => setValue("postalCode", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("الرقم الإضافي", "Secondary number")}</Label>
                        <Input
                          placeholder={t("مثال: 8799", "e.g. 8799")}
                          value={information?.secondaryNumber ?? ""}
                          onChange={(e) => setValue("information.secondaryNumber", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      {/* Compound (gated complex) metadata — optional, but
                          when set surfaces in the task context card so the
                          field employee knows where to enter and what code
                          to use at the gate. */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">
                            {t("اسم المجمع", "Compound name")}
                            <span className="text-muted-foreground ms-1">
                              {t("(اختياري)", "(optional)")}
                            </span>
                          </Label>
                          <Input
                            placeholder={t("اسم المجمع السكني", "Compound name")}
                            value={compoundName}
                            onChange={(e) => setValue("compoundName", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">
                            {t("كود المجمع", "Compound code")}
                            <span className="text-muted-foreground ms-1">
                              {t("(اختياري)", "(optional)")}
                            </span>
                          </Label>
                          <Input
                            placeholder={t("كود البوابة الرئيسية", "Main gate code")}
                            value={compoundCode}
                            onChange={(e) => setValue("compoundCode", e.target.value)}
                            className="h-9 text-sm"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Property Information Section */}
                    <div className="space-y-3 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">{t("معلومات العقار", "Property Information")}</h5>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("عرض الشارع", "Street width")}</Label>
                        <Input
                          placeholder={t("أدخل عرض الشارع", "Enter street width")}
                          value={information?.streetWidth ?? ""}
                          onChange={(e) => setValue("information.streetWidth", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("رقم الأرض", "Land number")}</Label>
                        <Input
                          placeholder={t("أدخل رقم الأرض", "Enter land number")}
                          value={information?.landNumber ?? ""}
                          onChange={(e) => setValue("information.landNumber", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("رقم المخطط", "Plan number")}</Label>
                        <Input
                          placeholder={t("أدخل رقم المخطط", "Enter plan number")}
                          value={information?.planNumber ?? ""}
                          onChange={(e) => setValue("information.planNumber", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("عمر العقار", "Property age")}</Label>
                        <Select value={information?.propertyAge ?? ""} onValueChange={(v) => setValue("information.propertyAge", v)}>
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue placeholder={t("اختر عمر العقار", "Select property age")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">{t("جديد", "New")}</SelectItem>
                            <SelectItem value="1-5">{t("1-5 سنوات", "1-5 years")}</SelectItem>
                            <SelectItem value="5-10">{t("5-10 سنوات", "5-10 years")}</SelectItem>
                            <SelectItem value="10+">{t("أكثر من 10 سنوات", "More than 10 years")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("الاتجاه", "Direction")}</Label>
                        <Select value={information?.direction ?? ""} onValueChange={(v) => setValue("information.direction", v)}>
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue placeholder={t("اختر الاتجاه", "Select direction")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="north">{t("شمال", "North")}</SelectItem>
                            <SelectItem value="south">{t("جنوب", "South")}</SelectItem>
                            <SelectItem value="east">{t("شرق", "East")}</SelectItem>
                            <SelectItem value="west">{t("غرب", "West")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("قيمة العقار", "Property value")}</Label>
                        <Input
                          type="number"
                          placeholder={t("أدخل قيمة العقار", "Enter property value")}
                          value={information?.propertyValue ?? ""}
                          onChange={(e) => setValue("information.propertyValue", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("اسم المجتمع", "Community name")}</Label>
                        <Input
                          placeholder={t("أدخل اسم المجتمع", "Enter community name")}
                          value={information?.communityName ?? ""}
                          onChange={(e) => setValue("information.communityName", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* General Specifications */}
                    <div className="space-y-2 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">{t("المواصفات العامة", "General Specifications")}</h5>
                      <CounterInput
                        label={t("عدد الطوابق", "Number of Floors")}
                        value={information?.floorsCount ?? 1}
                        onChange={(v) => setValue("information.floorsCount", v)}
                      />
                      <CounterInput
                        label={t("عدد الوحدات لكل طابق", "Units Per Floor")}
                        value={information?.unitsPerFloor ?? 1}
                        onChange={(v) => setValue("information.unitsPerFloor", v)}
                      />
                      <CounterInput
                        label={t("عدد مواقف السيارات", "Number of Parking Lots")}
                        value={information?.parkingLots ?? 1}
                        onChange={(v) => setValue("information.parkingLots", v)}
                      />
                      <CounterInput
                        label={t("عدد المصاعد", "Number of Elevators")}
                        value={information?.elevators ?? 1}
                        onChange={(v) => setValue("information.elevators", v)}
                      />
                    </div>

                    {/* Attachments */}
                    <div className="space-y-2 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">{t("المرفقات", "Attachments")}</h5>
                      <ImageUpload
                        label={t("مخطط الطابق", "Floor plan")}
                        hint={t("رفع الملفات والصور", "Upload files and photos")}
                        folder="properties"
                        companyId={companyId}
                        value={floorPlanImages}
                        onChange={(v) => setValue("floorPlanImages", v)}
                        maxFiles={3}
                      />
                    </div>
                  </div>
                )}

                {activeInfoTab === "utilities" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">
                      {t("معلومات المرافق والخدمات المتوفرة", "Information on Available Utilities and Services")}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {UTILITIES.map((utility) => (
                        <Button
                          key={utility.id}
                          type="button"
                          variant={selectedUtilities.includes(utility.id) ? "default" : "outline"}
                          size="sm"
                          className="rounded-full"
                          onClick={() => toggleUtility(utility.id)}
                        >
                          {t(utility.labelAr, utility.labelEn)}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {activeInfoTab === "highlights" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">
                      {t("مميزات العقار", "Property Highlights")}
                    </h4>
                    <Accordion type="multiple" className="w-full">
                      {HIGHLIGHTS_CATEGORIES.map((category) => (
                        <AccordionItem key={category.id} value={category.id}>
                          <AccordionTrigger className="text-sm">
                            {t(category.nameAr, category.nameEn)}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="grid grid-cols-2 gap-2 pt-2">
                              {category.items.map((item) => (
                                <label key={item.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                  <Checkbox
                                    checked={selectedHighlights[category.id]?.includes(item.id) || false}
                                    onCheckedChange={(checked) => {
                                      const current = selectedHighlights[category.id] || [];
                                      setValue("highlights", {
                                        ...selectedHighlights,
                                        [category.id]: checked
                                          ? [...current, item.id]
                                          : current.filter((id) => id !== item.id),
                                      });
                                    }}
                                  />
                                  <span>{t(item.nameAr, item.nameEn)}</span>
                                </label>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                )}

                {activeInfoTab === "legal" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">
                      {t("المعلومات القانونية", "Legal Information")}
                    </h4>

                    {/* Ownership Type Dropdown */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        {t("نوع الملكية", "Ownership Type")} <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={legalValues?.ownershipType ?? ""}
                        onValueChange={(v) => {
                          setValue("legal.ownershipType", v, { shouldValidate: true });
                        }}
                      >
                        <SelectTrigger className={cn("h-9 text-sm", errors.legal?.ownershipType && "border-destructive")}>
                          <SelectValue placeholder={t("اختر نوع الملكية", "Select ownership type")} />
                        </SelectTrigger>
                        <SelectContent>
                          {OWNERSHIP_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {t(type.labelAr, type.labelEn)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.legal?.ownershipType && (
                        <p className="text-destructive text-xs mt-1">
                          {t("نوع الملكية مطلوب", "Ownership type is required")}
                        </p>
                      )}
                    </div>

                    {/* Ownership Number */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        {t("رقم الملكية", "Ownership Number")} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={legalValues?.ownershipNumber ?? ""}
                        onChange={(e) => setValue("legal.ownershipNumber", e.target.value, { shouldValidate: true })}
                        placeholder={t("أدخل رقم الملكية", "Enter ownership number")}
                        className={cn("h-9 text-sm", errors.legal?.ownershipNumber && "border-destructive")}
                      />
                      {errors.legal?.ownershipNumber && (
                        <p className="text-destructive text-xs mt-1">
                          {t("رقم الملكية مطلوب", "Ownership number is required")}
                        </p>
                      )}
                    </div>

                    {/* Ownership Date — Gregorian + Hijri (F11) */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        {t("تاريخ الملكية (ميلادي)", "Ownership Date (Gregorian)")} <span className="text-destructive">*</span>
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-start h-9 text-sm font-normal",
                              errors.legal?.ownershipDate && "border-destructive",
                            )}
                          >
                            <CalendarIcon className="me-2 h-4 w-4" />
                            {ownershipDateObj ? format(ownershipDateObj, "dd/MM/yyyy") : t("اختر التاريخ", "Select date")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={ownershipDateObj}
                            onSelect={(d) => {
                              setOwnershipDateObj(d);
                              setValue("legal.ownershipDate", d?.toISOString() ?? "", { shouldValidate: true });
                              // Derive the Umm al-Qura Hijri deed date from the Gregorian one.
                              setOwnershipDateHijri(d ? hijriDateString(format(d, "yyyy-MM-dd")) ?? "" : "");
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      {errors.legal?.ownershipDate && (
                        <p className="text-destructive text-xs mt-1">
                          {t("تاريخ الملكية مطلوب", "Ownership date is required")}
                        </p>
                      )}
                    </div>

                    {/* F11: Hijri deed date (property level — optional) */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("تاريخ الملكية (هجري)", "Ownership Date (Hijri)")}</Label>
                      <Input
                        placeholder={t("مثال: 1445/09/15", "e.g. 1445/09/15")}
                        value={ownershipDateHijri}
                        readOnly
                        className="h-9 text-sm bg-muted"
                        dir="ltr"
                      />
                    </div>

                    {/* Conditional Checkboxes Section */}
                    <div className="space-y-3 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">{t("القيود والالتزامات", "Restrictions and Obligations")}</h5>

                      {/* Info Restriction */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={!!legalValues?.infoRestriction}
                            onCheckedChange={(c) => setValue("legal.infoRestriction", c ? " " : null)}
                          />
                          <Label className="text-sm font-normal">{t("قيد معلومات", "Info Restriction")}</Label>
                        </div>
                        {legalValues?.infoRestriction && (
                          <Input
                            value={legalValues.infoRestriction}
                            onChange={(e) => setValue("legal.infoRestriction", e.target.value)}
                            placeholder={t("أدخل قيد المعلومات", "Enter info restriction")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>

                      {/* Mortgage Restriction */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={!!legalValues?.mortgageRestriction}
                            onCheckedChange={(c) => setValue("legal.mortgageRestriction", c ? " " : null)}
                          />
                          <Label className="text-sm font-normal">{t("قيد رهن", "Mortgage Restriction")}</Label>
                        </div>
                        {legalValues?.mortgageRestriction && (
                          <Input
                            value={legalValues.mortgageRestriction}
                            onChange={(e) => setValue("legal.mortgageRestriction", e.target.value)}
                            placeholder={t("أدخل قيد الرهن", "Enter mortgage restriction")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>

                      {/* Obligations */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={!!legalValues?.obligations}
                            onCheckedChange={(c) => setValue("legal.obligations", c ? " " : null)}
                          />
                          <Label className="text-sm font-normal">{t("التزامات", "Obligations")}</Label>
                        </div>
                        {legalValues?.obligations && (
                          <Input
                            value={legalValues.obligations}
                            onChange={(e) => setValue("legal.obligations", e.target.value)}
                            placeholder={t("أدخل الالتزامات", "Enter obligations")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>

                      {/* Property Dispute */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={!!legalValues?.propertyDispute}
                            onCheckedChange={(c) => setValue("legal.propertyDispute", c ? " " : null)}
                          />
                          <Label className="text-sm font-normal">{t("نزاع على العقار", "Property Dispute")}</Label>
                        </div>
                        {legalValues?.propertyDispute && (
                          <Input
                            value={legalValues.propertyDispute}
                            onChange={(e) => setValue("legal.propertyDispute", e.target.value)}
                            placeholder={t("أدخل تفاصيل النزاع", "Enter property dispute")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>
                    </div>

                    {/* Ownership Documents Upload — F8: PDF accepted */}
                    <div className="pt-2">
                      <ImageUpload
                        label={t("وثائق الملكية", "Ownership Documents")}
                        hint={t("رفع الوثائق والمستندات القانونية (يُقبل PDF)", "Upload legal documents (PDF accepted)")}
                        folder="properties"
                        companyId={companyId}
                        value={legalValues?.ownershipDocuments ?? []}
                        onChange={(v) => setValue("legal.ownershipDocuments", v)}
                        maxFiles={5}
                        accept={IMAGE_OR_PDF_ACCEPT}
                      />
                    </div>
                  </div>
                )}

                {activeInfoTab === "publicPage" && (
                  <div className="space-y-5 mt-4">
                    <h4 className="text-primary font-semibold text-sm">
                      {t("إعدادات الصفحة العامة", "Public Page Settings")}
                    </h4>
                    <p className="text-xs text-muted-foreground -mt-2">
                      {t(
                        "تظهر هذه الإعدادات في صفحة العقار العامة التي يمكن مشاركتها مع العملاء.",
                        "These settings appear on the property's public page that can be shared with clients.",
                      )}
                    </p>

                    {/* Public description (bilingual) */}
                    <div className="space-y-3">
                      <h5 className="text-primary/80 font-medium text-xs">
                        {t("وصف العقار", "Property Description")}
                      </h5>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("الوصف (عربي)", "Description (Arabic)")}</Label>
                        <Textarea
                          placeholder={t("أدخل وصف العقار الذي سيظهر في الصفحة العامة", "Enter the property description shown on the public page")}
                          value={description}
                          onChange={(e) => setValue("description", e.target.value)}
                          rows={4}
                          className="text-sm"
                          dir="rtl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("الوصف (إنجليزي)", "Description (English)")}</Label>
                        <Textarea
                          placeholder={t("أدخل الوصف بالإنجليزية", "Enter the English description")}
                          value={descriptionEn}
                          onChange={(e) => setValue("descriptionEn", e.target.value)}
                          rows={4}
                          className="text-sm"
                          dir="ltr"
                        />
                      </div>
                    </div>

                    {/* Available Units section source */}
                    <div className="space-y-3">
                      <h5 className="text-primary/80 font-medium text-xs">
                        {t("قسم الوحدات المتاحة", "Available Units Section")}
                      </h5>
                      <p className="text-xs text-muted-foreground">
                        {t(
                          "اختر ما الذي يظهر في قسم الوحدات المتاحة على الصفحة العامة.",
                          "Choose what appears in the Available Units section on the public page.",
                        )}
                      </p>
                      <RadioGroup
                        value={publicUnitsSource}
                        onValueChange={(v) => setValue("publicUnitsSource", v as "listings" | "vacant_units")}
                        className="gap-2"
                      >
                        <label
                          htmlFor="units-source-listings"
                          className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/40"
                        >
                          <RadioGroupItem value="listings" id="units-source-listings" className="mt-0.5" />
                          <span className="space-y-0.5">
                            <span className="block text-sm font-medium">{t("الإعلانات", "Listings")}</span>
                            <span className="block text-xs text-muted-foreground">
                              {t(
                                "عرض الإعلانات النشطة لهذا العقار (الوضع الافتراضي).",
                                "Show this property's active listings (default).",
                              )}
                            </span>
                          </span>
                        </label>
                        <label
                          htmlFor="units-source-vacant"
                          className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/40"
                        >
                          <RadioGroupItem value="vacant_units" id="units-source-vacant" className="mt-0.5" />
                          <span className="space-y-0.5">
                            <span className="block text-sm font-medium">{t("الوحدات الشاغرة", "Vacant Units")}</span>
                            <span className="block text-xs text-muted-foreground">
                              {t(
                                "عرض الوحدات المتاحة (الشاغرة) في هذا العقار مباشرة.",
                                "Show this property's available (vacant) units directly.",
                              )}
                            </span>
                          </span>
                        </label>
                      </RadioGroup>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* UNIT INFO TAB */}
            <TabsContent value="unit" className="p-4 mt-0 space-y-4">
              {/* List of already-added units */}
              {pendingUnits.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium">{t("الوحدات المضافة", "Added Units")}</h5>
                  <div className="space-y-1">
                    {pendingUnits.map((unit, index) => (
                      <div key={index} className={`flex items-center justify-between p-2 rounded-lg text-sm ${editingUnitIndex === index ? "bg-primary/10 ring-1 ring-primary" : ownershipType === "multiple" && !unit.owner_id ? "bg-destructive/10 ring-1 ring-destructive/30" : "bg-muted/30"}`}>
                        <span className="flex items-center gap-1.5">
                          {unit.unitName || `${t("وحدة", "Unit")} ${index + 1}`}
                          {ownershipType === "multiple" && !unit.owner_id && (
                            <span className="text-[10px] text-destructive">{t("بدون مالك", "No owner")}</span>
                          )}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => loadUnitForEdit(index)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive"
                            onClick={() => {
                              setPendingUnits((prev) => prev.filter((_, i) => i !== index));
                              if (editingUnitIndex === index) {
                                setEditingUnitIndex(null);
                                resetUnitFields();
                              }
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Basic Unit Info */}
              <div className="space-y-3">
                {/* Unit Purpose */}
                {/* Locked to the property purpose for single-purpose properties; */}
                {/* editable per-unit when the property is multi_purpose so a building/ */}
                {/* compound/commercial_center can mix residential and commercial units. */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("غرض الوحدة", "Unit purpose")} <span className="text-destructive">*</span>
                  </Label>
                  {propertyPurpose === "multi_purpose" ? (
                    <Select
                      value={unitPurpose}
                      onValueChange={(v) => {
                        setValue("unitPurpose", v);
                        // Same matrix-key dependency as handlePropertyTypeChange;
                        // pre-pick the first valid unit type so the dropdown is
                        // never empty after switching the unit purpose.
                        const unitTypes = resolveAvailableUnitTypes(v, propertyType);
                        setValue("unitType", unitTypes[0] ?? "");
                      }}
                    >
                      <SelectTrigger className={cn(errors.unitPurpose && "border-destructive")}>
                        <SelectValue placeholder={t("اختر غرض الوحدة", "Select unit purpose")} />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_PURPOSES.filter((p) => p.value !== "multi_purpose").map((purpose) => (
                          <SelectItem key={purpose.value} value={purpose.value}>
                            {language === "ar" ? purpose.labelAr : purpose.labelEn}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={propertyPurpose} disabled>
                      <SelectTrigger className="opacity-70">
                        <SelectValue placeholder={t("اختر غرض الوحدة", "Select unit purpose")} />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_PURPOSES.filter((p) => p.value !== "multi_purpose").map((purpose) => (
                          <SelectItem key={purpose.value} value={purpose.value}>
                            {language === "ar" ? purpose.labelAr : purpose.labelEn}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Unit Type */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("نوع الوحدة", "Unit type")} <span className="text-destructive">*</span>
                  </Label>
                  <Select value={unitType} onValueChange={(v) => setValue("unitType", v)}>
                    <SelectTrigger data-testid="unit-type-trigger" className={cn(errors.unitType && "border-destructive")}>
                      <SelectValue placeholder={t("اختر نوع الوحدة", "Select unit type")} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUnitTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {getUnitTypeLabel(type, language as "ar" | "en")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.unitType && <p className="text-destructive text-xs mt-1">{t("نوع الوحدة مطلوب", "Unit type is required")}</p>}
                </div>

                {/* Unit Name */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("اسم الوحدة", "Unit name")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    data-testid="unit-name-input"
                    placeholder={t("أدخل اسم الوحدة", "Enter unit name")}
                    value={unitName}
                    onChange={(e) => setValue("unitName", e.target.value)}
                    className={cn(errors.unitName && "border-destructive")}
                  />
                  {errors.unitName && <p className="text-destructive text-xs mt-1">{t("اسم الوحدة مطلوب", "Unit name is required")}</p>}
                </div>

                {/* Unit Owner (multi-owner properties only) */}
                {ownershipType === "multiple" && (
                  <div className="space-y-1.5">
                    <Label className="text-sm">
                      {t("مالك الوحدة", "Unit owner")} <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex gap-2 items-stretch">
                      <Select value={unitOwnerId ?? "none"} onValueChange={(v) => setValue("unitOwnerId", v === "none" ? null : v)}>
                        <SelectTrigger className={cn("flex-1", errors.unitOwnerId && "border-destructive")}>
                          <SelectValue placeholder={t("اختر مالك الوحدة", "Select unit owner")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("بدون مالك", "No owner")}</SelectItem>
                          {owners.map((owner) => {
                            const meta = ownerMeta[owner.id];
                            const isOrg = isOrganizationOwner(meta);
                            const rep = isOrg ? getOwnerRepresentativeName(meta, language) : "";
                            return (
                              <SelectItem key={owner.id} value={owner.id}>
                                <span className="flex items-center gap-2">
                                  {ownerTypeChip(meta)}
                                  <span>{getOwnerEntityDisplayName(owner, meta, language)}</span>
                                  {rep && <span className="text-xs text-muted-foreground">· {rep}</span>}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {canCreateOwners && (
                        <Button type="button" variant="outline" size="sm" className="shrink-0 h-11"
                          onClick={() => { setAddOwnerTarget("unit"); setShowAddOwnerDialog(true); }}>
                          {t("إضافة مالك", "Add New Owner")}
                        </Button>
                      )}
                    </div>
                    {errors.unitOwnerId && <p className="text-destructive text-xs mt-1">{t("مالك الوحدة مطلوب", "Unit owner is required")}</p>}
                  </div>
                )}

                {/* Unit Area */}
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("المساحة (م²)", "Area (Sq. M)")}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    placeholder={t("أدخل المساحة", "Enter area")}
                    value={unitArea}
                    onChange={(e) => setValue("unitArea", e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') e.preventDefault(); }}
                  />
                </div>

                {/* Unit Images */}
                <ImageUpload
                  label={t("صور الوحدة", "Unit images")}
                  hint={t("رفع الصور والفيديوهات", "Upload photos and videos")}
                  folder="units"
                  companyId={companyId}
                  value={unitImages}
                  onChange={(v) => setValue("unitImages", v)}
                  maxFiles={MAX_GALLERY_IMAGES}
                  accept={IMAGE_OR_VIDEO_ACCEPT}
                />
              </div>

              {/* Sub-tabs: Information, Utilities, Highlights, Legal (conditional) */}
              <div className="pt-2">
                <div className="flex gap-2 flex-wrap justify-center">
                  {["information", "utilities", "highlights"].map((tab) => (
                    <Button
                      key={tab}
                      type="button"
                      variant={unitActiveInfoTab === tab ? "default" : "outline"}
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => setUnitActiveInfoTab(unitActiveInfoTab === tab ? "" : tab)}
                    >
                      {unitActiveInfoTab === tab ? (
                        <Minus className="h-3 w-3 me-1" />
                      ) : (
                        <Plus className="h-3 w-3 me-1" />
                      )}
                      {tab === "information" && t("المعلومات", "Information")}
                      {tab === "utilities" && t("المرافق", "Utilities")}
                      {tab === "highlights" && t("المميزات", "Highlights")}
                    </Button>
                  ))}

                  {/* Conditional Legal tab - only show when ownershipType === "multiple" */}
                  {ownershipType === "multiple" && (
                    <Button
                      type="button"
                      variant={unitActiveInfoTab === "legal" ? "default" : "outline"}
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => setUnitActiveInfoTab(unitActiveInfoTab === "legal" ? "" : "legal")}
                    >
                      {unitActiveInfoTab === "legal" ? (
                        <Minus className="h-3 w-3 me-1" />
                      ) : (
                        <Plus className="h-3 w-3 me-1" />
                      )}
                      {t("القانونية", "Legal")}
                    </Button>
                  )}
                </div>

                {unitActiveInfoTab === "information" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">{t("المعلومات", "Information")}</h4>

                    {/* Financial Information */}
                    <div className="space-y-3">
                      <h5 className="text-primary/80 font-medium text-xs">{t("المعلومات المالية", "Financial Information")}</h5>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("الإيجار السنوي", "Annual rent")}</Label>
                        <Input
                          type="number"
                          placeholder={t("أدخل الإيجار السنوي", "Enter annual rent")}
                          value={annualRent}
                          onChange={(e) => setValue("annualRent", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("مبلغ التأمين", "Deposit")}</Label>
                        <Input
                          type="number"
                          placeholder={t("أدخل مبلغ التأمين", "Enter deposit amount")}
                          value={deposit}
                          onChange={(e) => setValue("deposit", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("مبلغ البيع", "Sale amount")}</Label>
                        <Input
                          type="number"
                          placeholder={t("أدخل مبلغ البيع", "Enter sale amount")}
                          value={saleAmount}
                          onChange={(e) => setValue("saleAmount", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* General Details */}
                    <div className="space-y-3 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">{t("التفاصيل العامة", "General Details")}</h5>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("رقم الطابق", "Floor number")}</Label>
                        <Input
                          type="number"
                          placeholder={t("أدخل رقم الطابق", "Enter floor number")}
                          value={floorNumber}
                          onChange={(e) => setValue("floorNumber", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="mezzanine"
                          checked={isMezzanine}
                          onCheckedChange={(checked) => setValue("isMezzanine", checked as boolean)}
                        />
                        <Label htmlFor="mezzanine" className="text-xs font-normal">
                          {t("ميزانين", "Mezzanine")}
                        </Label>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("طول الواجهة (م)", "Front side length (M)")}</Label>
                        <Input
                          type="number"
                          placeholder={t("أدخل طول الواجهة بالأمتار", "Enter front side length in meters")}
                          value={frontSideLength}
                          onChange={(e) => setValue("frontSideLength", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* Finishing */}
                    <div className="space-y-2 pt-2">
                      <Label className="text-xs font-medium">{t("التشطيب", "Finishing")}</Label>
                      <RadioGroup value={finishing} onValueChange={(v) => setValue("finishing", v)} className="flex gap-4">
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="shell" id="shell" />
                          <Label htmlFor="shell" className="text-xs font-normal">{t("عظم", "Shell")}</Label>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="finished" id="finished" />
                          <Label htmlFor="finished" className="text-xs font-normal">{t("مشطب", "Finished")}</Label>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="unfinished" id="unfinished" />
                          <Label htmlFor="unfinished" className="text-xs font-normal">{t("غير مشطب", "Unfinished")}</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Furnishing Status */}
                    <div className="space-y-2 pt-2">
                      <Label className="text-xs font-medium">{t("حالة الأثاث", "Furnishing status")}</Label>
                      <RadioGroup value={furnishingStatus} onValueChange={(v) => setValue("furnishingStatus", v)} className="flex gap-4">
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="new" id="furn-new" />
                          <Label htmlFor="furn-new" className="text-xs font-normal">{t("جديد", "New")}</Label>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="used" id="furn-used" />
                          <Label htmlFor="furn-used" className="text-xs font-normal">{t("مستعمل", "Used")}</Label>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="unfurnished" id="furn-unfurnished" />
                          <Label htmlFor="furn-unfurnished" className="text-xs font-normal">{t("بدون أثاث", "Unfurnished")}</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Internal Specifications */}
                    <div className="space-y-2 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">{t("المواصفات الداخلية", "Internal Specifications")}</h5>
                      <CounterInput label={t("غرف النوم", "Bed rooms")} value={bedrooms} onChange={(v) => setValue("bedrooms", v)} />
                      <CounterInput label={t("دورات المياه", "Bath rooms")} value={bathrooms} onChange={(v) => setValue("bathrooms", v)} />
                      <CounterInput label={t("المجالس", "Majles")} value={majles} onChange={(v) => setValue("majles", v)} />
                      <CounterInput label={t("المطابخ", "Kitchens")} value={kitchens} onChange={(v) => setValue("kitchens", v)} />
                      <CounterInput label={t("غرف الخادمة", "Maid rooms")} value={maidRooms} onChange={(v) => setValue("maidRooms", v)} />
                      <CounterInput label={t("غرف التخزين", "Storage rooms")} value={storageRooms} onChange={(v) => setValue("storageRooms", v)} />
                      <CounterInput label={t("المساحات الخارجية", "Outdoor areas")} value={outdoorAreas} onChange={(v) => setValue("outdoorAreas", v)} />
                    </div>
                  </div>
                )}

                {unitActiveInfoTab === "utilities" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">
                      {t("مرافق الوحدة", "Utilities")}
                    </h4>

                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("حساب الكهرباء", "Electricity Account")}</Label>
                        <Input
                          placeholder={t("أدخل حساب الكهرباء", "Enter electricity account")}
                          value={unitElectricityAccount}
                          onChange={(e) => setValue("unitElectricityAccount", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("حساب المياه", "Water Account")}</Label>
                        <Input
                          placeholder={t("أدخل حساب المياه", "Enter water account")}
                          value={unitWaterAccount}
                          onChange={(e) => setValue("unitWaterAccount", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("رقم عداد الغاز", "Gas Meter Number")}</Label>
                        <Input
                          placeholder={t("أدخل رقم عداد الغاز", "Enter gas meter number")}
                          value={unitGasMeter}
                          onChange={(e) => setValue("unitGasMeter", e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {unitActiveInfoTab === "highlights" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">
                      {t("مميزات الوحدة", "Unit Highlights")}
                    </h4>

                    {/* Counter Controls */}
                    <div className="space-y-2">
                      <h5 className="text-primary/80 font-medium text-xs">
                        {t("التبريد والتكييف", "Cooling & AC")}
                      </h5>
                      <CounterInput label={t("تكييف مركزي", "Central AC")} value={unitCentralAC} onChange={(v) => setValue("unitCentralAC", v)} />
                      <CounterInput label={t("تكييف سبليت", "Split AC")} value={unitSplitAC} onChange={(v) => setValue("unitSplitAC", v)} />
                      <CounterInput label={t("مبرد صحراوي", "Desert Cooler")} value={unitDesertCooler} onChange={(v) => setValue("unitDesertCooler", v)} />
                      <CounterInput label={t("تكييف شباك", "Window AC")} value={unitWindowAC} onChange={(v) => setValue("unitWindowAC", v)} />
                      <CounterInput label={t("عدد المواقف", "Count of Parking")} value={unitParkingCount} onChange={(v) => setValue("unitParkingCount", v)} />
                    </div>

                    {/* Checkbox Controls */}
                    <div className="space-y-2 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">
                        {t("المميزات الإضافية", "Additional Features")}
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitHasHeating} onCheckedChange={(c) => setValue("unitHasHeating", !!c)} />
                          <span>{t("تدفئة", "Heating")}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitHasKitchenCabinets} onCheckedChange={(c) => setValue("unitHasKitchenCabinets", !!c)} />
                          <span>{t("خزائن مطبخ", "Kitchen Cabinets")}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitHasKitchenSink} onCheckedChange={(c) => setValue("unitHasKitchenSink", !!c)} />
                          <span>{t("حوض مطبخ", "Kitchen Sink")}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitHasDriversRoom} onCheckedChange={(c) => setValue("unitHasDriversRoom", !!c)} />
                          <span>{t("غرفة سائق", "Drivers Room")}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitIsSmartHome} onCheckedChange={(c) => setValue("unitIsSmartHome", !!c)} />
                          <span>{t("منزل ذكي", "Smart Home")}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitHasBalcony} onCheckedChange={(c) => setValue("unitHasBalcony", !!c)} />
                          <span>{t("شرفة أو تراس", "Balcony or Terrace")}</span>
                        </label>
                      </div>
                    </div>

                    {/* Parking Type */}
                    <div className="space-y-2 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">
                        {t("نوع الموقف", "Parking Type")}
                      </h5>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitHasParkingInside} onCheckedChange={(c) => setValue("unitHasParkingInside", !!c)} />
                          <span>{t("موقف داخلي", "Inside Parking")}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={unitHasParkingOutside} onCheckedChange={(c) => setValue("unitHasParkingOutside", !!c)} />
                          <span>{t("موقف خارجي", "Outside Parking")}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Conditional Unit Legal Tab */}
                {unitActiveInfoTab === "legal" && ownershipType === "multiple" && (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-primary font-semibold text-sm">
                      {t("المعلومات القانونية للوحدة", "Unit Legal Information")}
                    </h4>

                    {/* Ownership Type Dropdown */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("نوع الملكية", "Ownership Type")}</Label>
                      <Select value={unitLegalOwnershipType} onValueChange={(v) => setValue("unitLegalOwnershipType", v)}>
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder={t("اختر نوع الملكية", "Select ownership type")} />
                        </SelectTrigger>
                        <SelectContent>
                          {OWNERSHIP_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {t(type.labelAr, type.labelEn)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Ownership Number */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("رقم الملكية", "Ownership Number")}</Label>
                      <Input
                        value={unitOwnershipNumber}
                        onChange={(e) => setValue("unitOwnershipNumber", e.target.value)}
                        placeholder={t("أدخل رقم الملكية", "Enter ownership number")}
                        className="h-9 text-sm"
                      />
                    </div>

                    {/* Ownership Date — Gregorian + Hijri (F11) */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("تاريخ الملكية (ميلادي)", "Ownership Date (Gregorian)")}</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-start h-9 text-sm font-normal">
                              <CalendarIcon className="me-2 h-4 w-4" />
                              {unitOwnershipDateObj ? format(unitOwnershipDateObj, "dd/MM/yyyy") : t("اختر التاريخ", "Select date")}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={unitOwnershipDateObj}
                              onSelect={(d) => {
                                setUnitOwnershipDateObj(d);
                                setValue("unitOwnershipDate", d?.toISOString() ?? "");
                                // Derive the Umm al-Qura Hijri deed date from the Gregorian one.
                                setUnitOwnershipDateHijri(d ? hijriDateString(format(d, "yyyy-MM-dd")) ?? "" : "");
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("تاريخ الملكية (هجري)", "Ownership Date (Hijri)")}</Label>
                        <Input
                          placeholder={t("مثال: 1445/09/15", "e.g. 1445/09/15")}
                          value={unitOwnershipDateHijri}
                          readOnly
                          className="h-9 text-sm bg-muted"
                          dir="ltr"
                        />
                      </div>
                    </div>

                    {/* Conditional Checkboxes */}
                    <div className="space-y-3 pt-2">
                      <h5 className="text-primary/80 font-medium text-xs">{t("القيود والالتزامات", "Restrictions and Obligations")}</h5>

                      {/* Info Restriction */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={unitHasInfoRestriction} onCheckedChange={(c) => setValue("unitHasInfoRestriction", !!c)} />
                          <Label className="text-sm font-normal">{t("قيد معلومات", "Info Restriction")}</Label>
                        </div>
                        {unitHasInfoRestriction && (
                          <Input
                            value={unitInfoRestrictionText}
                            onChange={(e) => setValue("unitInfoRestrictionText", e.target.value)}
                            placeholder={t("أدخل قيد المعلومات", "Enter info restriction")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>

                      {/* Mortgage Restriction */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={unitHasMortgageRestriction} onCheckedChange={(c) => setValue("unitHasMortgageRestriction", !!c)} />
                          <Label className="text-sm font-normal">{t("قيد رهن", "Mortgage Restriction")}</Label>
                        </div>
                        {unitHasMortgageRestriction && (
                          <Input
                            value={unitMortgageRestrictionText}
                            onChange={(e) => setValue("unitMortgageRestrictionText", e.target.value)}
                            placeholder={t("أدخل قيد الرهن", "Enter mortgage restriction")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>

                      {/* Obligations */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={unitHasObligations} onCheckedChange={(c) => setValue("unitHasObligations", !!c)} />
                          <Label className="text-sm font-normal">{t("التزامات", "Obligations")}</Label>
                        </div>
                        {unitHasObligations && (
                          <Input
                            value={unitObligationsText}
                            onChange={(e) => setValue("unitObligationsText", e.target.value)}
                            placeholder={t("أدخل الالتزامات", "Enter obligations")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>

                      {/* Property Dispute */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={unitHasPropertyDispute} onCheckedChange={(c) => setValue("unitHasPropertyDispute", !!c)} />
                          <Label className="text-sm font-normal">{t("نزاع على العقار", "Property Dispute")}</Label>
                        </div>
                        {unitHasPropertyDispute && (
                          <Input
                            value={unitPropertyDisputeText}
                            onChange={(e) => setValue("unitPropertyDisputeText", e.target.value)}
                            placeholder={t("أدخل تفاصيل النزاع", "Enter property dispute")}
                            className="h-9 text-sm"
                          />
                        )}
                      </div>
                    </div>

                    {/* Ownership Documents — F8: PDF accepted */}
                    <div className="pt-2">
                      <ImageUpload
                        label={t("وثائق الملكية", "Ownership Documents")}
                        hint={t("رفع الوثائق والمستندات القانونية (يُقبل PDF)", "Upload legal documents (PDF accepted)")}
                        folder="units"
                        companyId={companyId}
                        value={unitOwnershipDocuments}
                        onChange={(v) => setValue("unitOwnershipDocuments", v)}
                        maxFiles={5}
                        accept={IMAGE_OR_PDF_ACCEPT}
                      />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer Actions */}
        <div className="p-4 pt-0 border-t space-y-3 shrink-0">
          {/* Unit-specific controls - only on unit tab and not in edit mode */}
          {activeMainTab === "unit" && !isEditMode && (
            <div className="pb-3">
              <Button
                type="button"
                className="w-full gap-2 bg-success text-success-foreground hover:bg-success/90"
                onClick={handleAddUnit}
              >
                {editingUnitIndex !== null ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingUnitIndex !== null ? t("تحديث الوحدة", "Update Unit") : t("إضافة وحدة", "Add Unit")}
              </Button>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-end gap-3 pt-3">
            {activeMainTab === "property" || isEditMode ? (
              <Button variant="outline" onClick={handleClose} size="sm">
                {t("إلغاء", "Cancel")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setActiveMainTab("property")} size="sm">
                {t("رجوع", "Back")}
              </Button>
            )}

            {isEditMode ? (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed || !propertyName}
                size="sm"
                className="gap-2"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("حفظ التغييرات", "Save Changes")}
              </Button>
            ) : activeMainTab === "property" && canCreateUnits ? (
              <Button data-testid="property-next-btn" onClick={handleNext} disabled={!canProceed} size="sm">
                {t("التالي", "Next")}
              </Button>
            ) : activeMainTab === "property" && !canCreateUnits ? (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed || !propertyName}
                size="sm"
                className="gap-2"
                data-testid="property-submit"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("إرسال", "Submit")}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed || (pendingUnits.length === 0 && (!unitName.trim() || !unitType || (ownershipType === "multiple" && !unitOwnerId)))}
                size="sm"
                className="gap-2"
                data-testid="property-submit"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("إرسال", "Submit")}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Upgrade Modal */}
    <UpgradeModal
      open={showUpgradeModal}
      onOpenChange={setShowUpgradeModal}
      type="unit"
      current={limitInfo.current || existingUnitCount}
      limit={limitInfo.limit || (unitLimit === Infinity ? 0 : unitLimit)}
      requested={limitInfo.requested > 0 ? limitInfo.requested : undefined}
    />

    <UnsavedChangesDialog blocker={blocker} />

    {/* Nested Add Owner dialog */}
    <AddOwnerDialog
      open={showAddOwnerDialog}
      onOpenChange={setShowAddOwnerDialog}
      onSuccess={(newOwnerId) =>
        setValue(addOwnerTarget === "unit" ? "unitOwnerId" : "ownerId", newOwnerId)
      }
    />

    {/* Unit-owner auto-sync confirmation dialog (edit mode) */}
    <AlertDialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("تحديث مالك الوحدات؟", "Update unit owners?")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              `تحديث جميع الوحدات للمالك الجديد؟ سيؤدي ذلك إلى تعيين نفس المالك لجميع الوحدات.`,
              `Update all units to the new owner? This will set the same owner for all units.`
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => {
            // No — discard changes, don't save
            setShowSyncDialog(false);
            setSyncPendingPayload(null);
            toast.info(t("تم إلغاء التغييرات", "Changes discarded"));
          }}>
            {t("لا، إلغاء", "No, cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={async () => {
              // Yes — save property + sync all units to new owner
              if (syncPendingPayload) {
                await updateProperty.mutateAsync(syncPendingPayload as any);
                toast.success(t("تم تحديث العقار بنجاح", "Property updated successfully"));
              }
              if (syncPendingOwnerId && editProperty && companyId) {
                await unitsRepository.setOwnerByProperty(
                  editProperty.id,
                  companyId,
                  syncPendingOwnerId,
                );
                queryClient.invalidateQueries({ queryKey: ["units"] });
                queryClient.invalidateQueries({ queryKey: ["units-with-contracts"] });
                // Also propagate new owner to draft/pending contracts so they
                // reflect the current owner without requiring delete-and-recreate.
                await propagateDraftContractOwnerIfNeeded(
                  editProperty.id,
                  syncPendingOwnerId,
                  editProperty.owner_id,
                  companyId,
                  queryClient,
                );
              }
              if (editProperty && companyId) {
                await cascadeUnitPurposesIfNeeded(editProperty, propertyPurpose, companyId, queryClient);
              }
              setShowSyncDialog(false);
              setSyncPendingPayload(null);
              handleClose();
            }}
          >
            {t("نعم، حدّث", "Yes, update")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
